// Keep the styled basemap in one group so layer reordering preserves it.
const basemap = new ol.layer.Group();

// Initialize the map with OpenFreeMap Positron.
const map = new ol.Map({
    target: 'map',
    layers: [basemap],
    view: new ol.View({
        center: ol.proj.fromLonLat([0, 0]),
        zoom: 2
    }),
    controls: ol.control.defaults.defaults({ attribution: false })
});

olms.apply(basemap, 'https://tiles.openfreemap.org/styles/positron')
    .then(updateAttribution)
    .catch(error => {
        console.error('Error loading OpenFreeMap basemap:', error);
        showStatus('Unable to load the basemap. Please reload to try again.', true);
    });

// Store loaded layers for management with metadata
const loadedLayers = [];

// Track loaded datasets to prevent duplicates
const loadedDatasets = new Set();

// ===== CRS / Projection Support =====

// Register proj4 with OpenLayers so non-built-in CRS (anything beyond
// EPSG:4326/3857) can be transformed. Common Canadian CRS are predefined
// to avoid a network lookup; anything else is fetched from epsg.io on demand.
if (typeof proj4 !== 'undefined') {
    proj4.defs('EPSG:3979', '+proj=lcc +lat_0=49 +lon_0=-95 +lat_1=49 +lat_2=77 +x_0=0 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs');
    proj4.defs('EPSG:3978', '+proj=lcc +lat_0=49 +lon_0=-95 +lat_1=49 +lat_2=77 +x_0=0 +y_0=0 +datum=NAD83 +units=m +no_defs');
    ol.proj.proj4.register(proj4);
}

// Cache of CRS codes already resolved (or known to have failed)
const resolvedProjections = new Map();

// Ensure a projection like 'EPSG:3979' is usable, fetching its proj4
// definition from epsg.io if OpenLayers doesn't know it yet.
async function ensureProjection(code) {
    if (!code) return null;
    if (code === 'EPSG:4326' || code === 'EPSG:3857' || ol.proj.get(code)) {
        return code;
    }
    if (typeof proj4 === 'undefined') {
        throw new Error(`Projection ${code} requires the proj4 library, which failed to load`);
    }
    if (resolvedProjections.has(code)) {
        const cached = resolvedProjections.get(code);
        if (cached instanceof Error) throw cached;
        return cached;
    }
    const epsgCode = code.match(/^EPSG:(\d+)$/i)?.[1];
    if (!epsgCode) {
        const err = new Error(`Unsupported CRS identifier: ${code}`);
        resolvedProjections.set(code, err);
        throw err;
    }
    try {
        const response = await fetch(`https://epsg.io/${epsgCode}.proj4`);
        if (!response.ok) throw new Error(`epsg.io returned ${response.status}`);
        const def = (await response.text()).trim();
        if (!def.startsWith('+')) throw new Error('Invalid proj4 definition received');
        proj4.defs(code, def);
        ol.proj.proj4.register(proj4);
        resolvedProjections.set(code, code);
        return code;
    } catch (error) {
        const err = new Error(`Could not resolve projection ${code}: ${error.message}`);
        resolvedProjections.set(code, err);
        throw err;
    }
}

// Normalize CRS identifiers (URNs, legacy names) to 'EPSG:nnnn' form
function normalizeCrsName(name) {
    if (!name) return null;
    // urn:ogc:def:crs:OGC:1.3:CRS84 and similar are lon/lat WGS84
    if (/CRS84/i.test(name)) return 'EPSG:4326';
    // urn:ogc:def:crs:EPSG::3979 or urn:ogc:def:crs:EPSG:8.9:3979
    const urnMatch = name.match(/urn:ogc:def:crs:EPSG:[\d.]*:(\d+)/i);
    if (urnMatch) return `EPSG:${urnMatch[1]}`;
    // EPSG:3979 or http://www.opengis.net/def/crs/EPSG/0/3979
    const epsgMatch = name.match(/EPSG[/:](?:.*[/:])?(\d+)$/i);
    if (epsgMatch) return `EPSG:${epsgMatch[1]}`;
    return null;
}

// Extract CRS from a GeoJSON object's legacy `crs` member (written by
// GDAL/ogr2ogr etc). RFC 7946 dropped it, so absence means EPSG:4326.
function getGeoJSONCrs(geojsonData) {
    const name = geojsonData?.crs?.properties?.name;
    return normalizeCrsName(name);
}

// Extract an EPSG code from PROJJSON (used by GeoParquet metadata)
function getProjjsonCrs(projjson) {
    if (!projjson) return null;
    const id = projjson.id;
    if (id?.authority && id?.code) {
        return normalizeCrsName(`${id.authority}:${id.code}`);
    }
    return null;
}

// Heuristic: do coordinates plausibly look like lon/lat degrees?
function geojsonLooksLikeLonLat(geojsonData) {
    let checked = 0;
    let valid = true;
    const checkCoords = (coords) => {
        if (checked >= 20 || !valid) return;
        if (typeof coords[0] === 'number') {
            checked++;
            if (Math.abs(coords[0]) > 180 || Math.abs(coords[1]) > 90) valid = false;
        } else {
            for (const c of coords) checkCoords(c);
        }
    };
    const features = geojsonData.features || [geojsonData];
    for (const f of features) {
        if (checked >= 20 || !valid) break;
        const geom = f.geometry || f;
        if (geom?.coordinates) checkCoords(geom.coordinates);
    }
    return valid;
}

// Resolve the data projection for a GeoJSON payload: use embedded CRS if
// present, otherwise assume EPSG:4326 and warn if coordinates look wrong.
async function resolveGeoJSONProjection(geojsonData, sourceLabel) {
    const crs = getGeoJSONCrs(geojsonData);
    if (crs) {
        await ensureProjection(crs);
        if (crs !== 'EPSG:4326') {
            showStatus(`Reprojecting ${sourceLabel} from ${crs}...`);
        }
        return crs;
    }
    if (!geojsonLooksLikeLonLat(geojsonData)) {
        const warning = `Warning: ${sourceLabel} has no CRS metadata and coordinates are outside lon/lat range — it may not display correctly`;
        // Defer so the warning isn't overwritten by the loader's success message
        setTimeout(() => showStatus(warning, true), 0);
    }
    return 'EPSG:4326';
}

// Resolve the data projection from a CRS detected in format metadata
// (FlatGeobuf header, GeoParquet geo metadata), falling back to the
// GeoJSON heuristics when none was found
async function resolveDetectedProjection(detectedCrs, featureCollection, sourceLabel) {
    if (detectedCrs) {
        const crs = normalizeCrsName(detectedCrs) || detectedCrs;
        await ensureProjection(crs);
        if (crs !== 'EPSG:4326') {
            showStatus(`Reprojecting ${sourceLabel} from ${crs}...`);
        }
        return crs;
    }
    return resolveGeoJSONProjection(featureCollection, sourceLabel);
}

// ===== Colormap Presets =====

const COLORMAPS = {
    viridis: [
        [0, [68, 1, 84, 1]], [0.1, [72, 35, 116, 1]], [0.2, [64, 67, 135, 1]],
        [0.3, [52, 94, 141, 1]], [0.4, [33, 145, 140, 1]], [0.5, [42, 170, 120, 1]],
        [0.6, [94, 201, 98, 1]], [0.7, [158, 217, 56, 1]], [0.8, [210, 226, 27, 1]],
        [0.9, [253, 231, 37, 1]], [1, [253, 231, 37, 1]]
    ],
    inferno: [
        [0, [0, 0, 4, 1]], [0.1, [22, 11, 57, 1]], [0.2, [66, 10, 104, 1]],
        [0.3, [106, 23, 110, 1]], [0.4, [147, 38, 103, 1]], [0.5, [188, 55, 84, 1]],
        [0.6, [221, 81, 58, 1]], [0.7, [243, 118, 27, 1]], [0.8, [252, 165, 10, 1]],
        [0.9, [246, 215, 70, 1]], [1, [252, 255, 164, 1]]
    ],
    turbo: [
        [0, [48, 18, 59, 1]], [0.1, [70, 68, 173, 1]], [0.2, [62, 131, 236, 1]],
        [0.3, [32, 185, 215, 1]], [0.4, [30, 220, 150, 1]], [0.5, [82, 237, 83, 1]],
        [0.6, [159, 238, 44, 1]], [0.7, [217, 213, 34, 1]], [0.8, [249, 163, 26, 1]],
        [0.9, [243, 92, 17, 1]], [1, [189, 17, 9, 1]]
    ],
    terrain: [
        [0, [51, 51, 153, 1]], [0.15, [51, 102, 204, 1]], [0.25, [0, 153, 153, 1]],
        [0.35, [51, 204, 102, 1]], [0.5, [153, 204, 51, 1]], [0.65, [204, 204, 51, 1]],
        [0.75, [204, 153, 51, 1]], [0.85, [153, 102, 51, 1]], [0.95, [200, 200, 200, 1]],
        [1, [255, 255, 255, 1]]
    ],
    blues: [
        [0, [247, 251, 255, 1]], [0.125, [222, 235, 247, 1]], [0.25, [198, 219, 239, 1]],
        [0.375, [158, 202, 225, 1]], [0.5, [107, 174, 214, 1]], [0.625, [66, 146, 198, 1]],
        [0.75, [33, 113, 181, 1]], [0.875, [8, 81, 156, 1]], [1, [8, 48, 107, 1]]
    ],
    reds: [
        [0, [255, 245, 240, 1]], [0.125, [254, 224, 210, 1]], [0.25, [252, 187, 161, 1]],
        [0.375, [252, 146, 114, 1]], [0.5, [251, 106, 74, 1]], [0.625, [239, 59, 44, 1]],
        [0.75, [203, 24, 29, 1]], [0.875, [165, 15, 21, 1]], [1, [103, 0, 13, 1]]
    ]
};

function getColormapStyle(colormapName, hasNodata) {
    if (!colormapName || colormapName === 'none') return undefined;
    const stops = COLORMAPS[colormapName];
    if (!stops) return undefined;
    const interpolateExpr = ['interpolate', ['linear'], ['band', 1]];
    for (const [stop, color] of stops) {
        interpolateExpr.push(stop, color);
    }
    // When nodata is set, the GeoTIFF source adds an alpha band (band 2 for single-band data).
    // The custom color expression overrides OL's default nodata handling, so we must
    // check the alpha band ourselves to keep nodata pixels transparent.
    if (hasNodata) {
        return { color: ['case', ['==', ['band', 2], 0], [0, 0, 0, 0], interpolateExpr] };
    }
    return { color: interpolateExpr };
}

// ===== Accessibility Helpers =====

// Announce message to screen readers
function announceToScreenReader(message, priority = 'polite') {
    const announcer = document.getElementById('srAnnouncements');
    if (announcer) {
        announcer.setAttribute('aria-live', priority);
        announcer.textContent = message;
        // Clear after announcement
        setTimeout(() => {
            announcer.textContent = '';
        }, 1000);
    }
}

// Focus trap for modals
function trapFocus(element) {
    const focusableElements = element.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    function handleTabKey(e) {
        if (e.key !== 'Tab') return;

        if (e.shiftKey) {
            if (document.activeElement === firstFocusable) {
                lastFocusable.focus();
                e.preventDefault();
            }
        } else {
            if (document.activeElement === lastFocusable) {
                firstFocusable.focus();
                e.preventDefault();
            }
        }
    }

    element.addEventListener('keydown', handleTabKey);
    return () => element.removeEventListener('keydown', handleTabKey);
}

// ===== End Accessibility Helpers =====

// Color palette for vector layers
const vectorColorPalette = [
    '#007bff', // Blue
    '#28a745', // Green
    '#dc3545', // Red
    '#fd7e14', // Orange
    '#6f42c1', // Purple
    '#20c997', // Teal
    '#e83e8c', // Pink
    '#ffc107', // Yellow
    '#17a2b8', // Cyan
    '#6c757d'  // Gray
];
let colorPaletteIndex = 0;

function getNextVectorColor() {
    const color = vectorColorPalette[colorPaletteIndex];
    colorPaletteIndex = (colorPaletteIndex + 1) % vectorColorPalette.length;
    return color;
}

// Layer manager functions
function formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function getLayerName(url, type, file = null) {
    // If it's a local file, use the file name
    if (file) {
        return `📁 ${file.name} (${type})`;
    }

    // Otherwise, extract from URL
    try {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname;
        const filename = pathname.split('/').pop() || 'Unnamed Layer';
        return `${filename} (${type})`;
    } catch {
        return `${type} layer`;
    }
}

function updateLayerManager() {
    const layerList = document.getElementById('layerList');

    if (loadedLayers.length === 0) {
        layerList.innerHTML = '<div class="layer-empty" role="listitem">No layers loaded</div>';
        return;
    }

    layerList.innerHTML = '';

    // Reverse iteration to show layers in correct order (top to bottom in UI = top to bottom on map)
    for (let i = loadedLayers.length - 1; i >= 0; i--) {
        const layerInfo = loadedLayers[i];
        const layerItem = document.createElement('div');
        layerItem.className = 'layer-item';
        layerItem.draggable = true;
        layerItem.dataset.index = i;
        layerItem.setAttribute('role', 'listitem');

        const dragHandle = document.createElement('button');
        dragHandle.type = 'button';
        dragHandle.className = 'layer-drag-handle';
        dragHandle.textContent = '☰';
        dragHandle.setAttribute('aria-label', `Reorder ${layerInfo.name}. Use Alt+Up or Alt+Down to move.`);

        const visibilityIcon = document.createElement('button');
        visibilityIcon.type = 'button';
        visibilityIcon.className = 'layer-visibility';
        visibilityIcon.textContent = layerInfo.layer.getVisible() ? '👁' : '👁‍🗨';
        visibilityIcon.setAttribute('aria-label', layerInfo.layer.getVisible() ? `Hide ${layerInfo.name}` : `Show ${layerInfo.name}`);
        visibilityIcon.setAttribute('aria-pressed', layerInfo.layer.getVisible() ? 'true' : 'false');
        visibilityIcon.draggable = false;

        const layerName = document.createElement('span');
        layerName.className = 'layer-name';

        // Add file size for local files
        if (layerInfo.file) {
            const nameDiv = document.createElement('div');
            nameDiv.textContent = layerInfo.name;

            const sizeDiv = document.createElement('div');
            sizeDiv.style.fontSize = '10px';
            sizeDiv.style.opacity = '0.7';
            sizeDiv.textContent = formatFileSize(layerInfo.file.size);

            layerName.appendChild(nameDiv);
            layerName.appendChild(sizeDiv);
            layerName.title = `${layerInfo.file.name} (${formatFileSize(layerInfo.file.size)})`;
        } else if (layerInfo.type === 'stac' && layerInfo.stacMetadata) {
            // Display STAC metadata
            const nameDiv = document.createElement('div');
            nameDiv.textContent = layerInfo.name;

            const metaDiv = document.createElement('div');
            metaDiv.className = 'layer-stac-meta';
            metaDiv.style.fontSize = '10px';
            metaDiv.style.opacity = '0.7';

            const metaParts = [];
            if (layerInfo.stacMetadata.datetime) {
                metaParts.push(formatSTACDatetime(layerInfo.stacMetadata.datetime));
            }
            if (layerInfo.stacMetadata.cloudCover !== undefined) {
                metaParts.push(`Cloud: ${layerInfo.stacMetadata.cloudCover}%`);
            }
            metaDiv.textContent = metaParts.join(' | ') || 'STAC Item';

            layerName.appendChild(nameDiv);
            layerName.appendChild(metaDiv);
            layerName.title = layerInfo.stacMetadata.stacItemUrl || layerInfo.url;
        } else {
            layerName.textContent = layerInfo.name;
            layerName.title = layerInfo.url;
        }

        layerName.draggable = false;

        const editBtn = document.createElement('button');
        editBtn.type = 'button';
        editBtn.className = 'layer-edit';
        editBtn.textContent = '⚙';
        editBtn.setAttribute('aria-label', `Edit ${layerInfo.name} settings`);
        editBtn.draggable = false;

        const copyBtn = document.createElement('button');
        copyBtn.type = 'button';
        copyBtn.className = 'layer-copy';
        copyBtn.textContent = '📋';
        copyBtn.setAttribute('aria-label', `Copy ${layerInfo.name} URL to clipboard`);
        copyBtn.draggable = false;

        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'layer-remove';
        removeBtn.textContent = '×';
        removeBtn.setAttribute('aria-label', `Remove ${layerInfo.name}`);
        removeBtn.draggable = false;

        // Toggle visibility
        visibilityIcon.addEventListener('click', () => {
            const visible = !layerInfo.layer.getVisible();
            layerInfo.layer.setVisible(visible);
            visibilityIcon.textContent = visible ? '👁' : '👁‍🗨';
            visibilityIcon.setAttribute('aria-label', visible ? `Hide ${layerInfo.name}` : `Show ${layerInfo.name}`);
            visibilityIcon.setAttribute('aria-pressed', visible ? 'true' : 'false');
            announceToScreenReader(`${layerInfo.name} ${visible ? 'visible' : 'hidden'}`);
        });

        // Keyboard reordering (Alt+Up/Down)
        dragHandle.addEventListener('keydown', (e) => {
            if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
                e.preventDefault();
                const currentIndex = i;
                let newIndex;

                if (e.key === 'ArrowUp' && currentIndex < loadedLayers.length - 1) {
                    newIndex = currentIndex + 1;
                } else if (e.key === 'ArrowDown' && currentIndex > 0) {
                    newIndex = currentIndex - 1;
                } else {
                    return;
                }

                // Reorder layers array
                const [movedLayer] = loadedLayers.splice(currentIndex, 1);
                loadedLayers.splice(newIndex, 0, movedLayer);

                // Reorder on map
                const mapLayers = map.getLayers();
                const allLayers = mapLayers.getArray();
                const baseLayer = allLayers[0];
                mapLayers.clear();
                mapLayers.push(baseLayer);
                loadedLayers.forEach(info => mapLayers.push(info.layer));

                updateLayerManager();
                announceToScreenReader(`${layerInfo.name} moved ${e.key === 'ArrowUp' ? 'up' : 'down'}`);

                // Refocus the drag handle in the new position
                setTimeout(() => {
                    const newLayerList = document.getElementById('layerList');
                    const newItems = newLayerList.querySelectorAll('.layer-item');
                    const targetIndex = loadedLayers.length - 1 - newIndex;
                    if (newItems[targetIndex]) {
                        const newDragHandle = newItems[targetIndex].querySelector('.layer-drag-handle');
                        if (newDragHandle) newDragHandle.focus();
                    }
                }, 50);
            }
        });

        // Copy URL to clipboard
        copyBtn.addEventListener('click', async () => {
            try {
                // Try modern clipboard API first
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(layerInfo.url);
                } else {
                    // Fallback for older browsers or non-HTTPS contexts
                    const textArea = document.createElement('textarea');
                    textArea.value = layerInfo.url;
                    textArea.style.position = 'fixed';
                    textArea.style.left = '-999999px';
                    textArea.style.top = '-999999px';
                    document.body.appendChild(textArea);
                    textArea.focus();
                    textArea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textArea);
                }

                // Temporarily change icon to indicate success
                const originalText = copyBtn.textContent;
                copyBtn.textContent = '✓';
                copyBtn.title = 'Copied!';
                setTimeout(() => {
                    copyBtn.textContent = originalText;
                    copyBtn.title = 'Copy URL to clipboard';
                }, 1500);
            } catch (err) {
                console.error('Failed to copy URL:', err);
                showStatus('Failed to copy URL to clipboard', true);
            }
        });

        // Edit layer settings
        editBtn.addEventListener('click', () => {
            openLayerEditModal(layerInfo);
        });

        // Remove layer
        removeBtn.addEventListener('click', () => {
            const removedName = layerInfo.name;
            map.removeLayer(layerInfo.layer);
            loadedLayers.splice(i, 1);
            const datasetKey = `${layerInfo.type}:${layerInfo.url}`;
            loadedDatasets.delete(datasetKey);
            updateLayerManager();
            announceToScreenReader(`${removedName} removed`);
        });

        // Drag and drop - only allow drag from the drag handle
        dragHandle.addEventListener('mousedown', () => {
            layerItem.setAttribute('data-allow-drag', 'true');
        });

        layerItem.addEventListener('dragstart', (e) => {
            // Only allow drag if initiated from drag handle
            if (layerItem.getAttribute('data-allow-drag') !== 'true') {
                e.preventDefault();
                return;
            }
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', i);
            layerItem.classList.add('dragging');
        });

        layerItem.addEventListener('dragend', () => {
            layerItem.classList.remove('dragging');
            layerItem.removeAttribute('data-allow-drag');
        });

        layerItem.addEventListener('dragover', (e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
        });

        layerItem.addEventListener('drop', (e) => {
            e.preventDefault();
            const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
            const toIndex = i;

            if (fromIndex !== toIndex) {
                // Reorder layers array
                const [movedLayer] = loadedLayers.splice(fromIndex, 1);
                loadedLayers.splice(toIndex, 0, movedLayer);

                // Reorder on map (skip base layer at index 0)
                const mapLayers = map.getLayers();
                const allLayers = mapLayers.getArray();
                const baseLayer = allLayers[0];

                // Rebuild layer order
                mapLayers.clear();
                mapLayers.push(baseLayer);
                loadedLayers.forEach(info => mapLayers.push(info.layer));

                updateLayerManager();
            }
        });

        layerItem.appendChild(dragHandle);
        layerItem.appendChild(visibilityIcon);
        layerItem.appendChild(layerName);

        // Add info button for STAC layers to show full metadata
        if (layerInfo.type === 'stac' && layerInfo.stacMetadata) {
            const infoBtn = document.createElement('button');
            infoBtn.type = 'button';
            infoBtn.className = 'layer-info';
            infoBtn.textContent = 'i';
            infoBtn.setAttribute('aria-label', `View ${layerInfo.name} metadata`);
            infoBtn.draggable = false;
            infoBtn.addEventListener('click', () => {
                showSTACMetadataModal(layerInfo.stacMetadata);
            });
            layerItem.appendChild(infoBtn);
        }

        layerItem.appendChild(editBtn);

        // Only show copy button for URL-based layers (not local files)
        if (!layerInfo.file) {
            layerItem.appendChild(copyBtn);
        }

        layerItem.appendChild(removeBtn);
        layerList.appendChild(layerItem);
    }
}

function addLayerToManager(layer, url, type, strokeColor = null, fillColor = null, file = null, extra = {}) {
    const name = getLayerName(url, type, file);

    // Use provided colors or default to blue for non-vector layers
    const finalStrokeColor = strokeColor || '#007bff';
    const finalFillColor = fillColor || '#007bff';
    const opacity = 1.0; // Default full opacity
    const fillOpacity = 0.5; // Default fill opacity (50%)

    // Set layer opacity to full by default
    layer.setOpacity(opacity);

    loadedLayers.push({
        layer,
        url,
        file,
        type,
        name,
        strokeColor: finalStrokeColor,
        fillColor: finalFillColor,
        opacity,
        fillOpacity,
        colormap: 'none',
        hasNodata: false,
        ...extra
    });
    updateLayerManager();
    announceToScreenReader(`Layer ${name} added`);
}

// Update vector layer style with new colors
function updateLayerStyle(layerInfo) {
    const strokeColor = layerInfo.strokeColor || '#007bff';
    const fillColor = layerInfo.fillColor || '#007bff';
    const fillOpacity = layerInfo.fillOpacity !== undefined ? layerInfo.fillOpacity : 0.5;

    // Convert hex to rgba
    const strokeR = parseInt(strokeColor.slice(1, 3), 16);
    const strokeG = parseInt(strokeColor.slice(3, 5), 16);
    const strokeB = parseInt(strokeColor.slice(5, 7), 16);

    const fillR = parseInt(fillColor.slice(1, 3), 16);
    const fillG = parseInt(fillColor.slice(3, 5), 16);
    const fillB = parseInt(fillColor.slice(5, 7), 16);

    const strokeColorRGBA = `rgba(${strokeR}, ${strokeG}, ${strokeB}, 1)`;
    const fillColorRGBA = `rgba(${fillR}, ${fillG}, ${fillB}, ${fillOpacity})`;
    const pointFillColor = `rgba(${strokeR}, ${strokeG}, ${strokeB}, 1)`;

    const newStyle = new ol.style.Style({
        stroke: new ol.style.Stroke({
            color: strokeColorRGBA,
            width: 2
        }),
        fill: new ol.style.Fill({
            color: fillColorRGBA
        }),
        image: new ol.style.Circle({
            radius: 8,
            fill: new ol.style.Fill({
                color: pointFillColor
            }),
            stroke: new ol.style.Stroke({
                color: 'rgba(255, 255, 255, 1)',
                width: 1
            })
        })
    });

    // Update the layer style
    if (layerInfo.layer instanceof ol.layer.WebGLVector) {
        layerInfo.layer.setStyle(createWebGLVectorStyle(strokeColor, fillColor, fillOpacity));
    } else if (typeof layerInfo.layer.setStyle === 'function') {
        layerInfo.layer.setStyle(newStyle);
    }
}

// Update layer opacity
function updateLayerOpacity(layerInfo, opacity) {
    layerInfo.opacity = opacity;
    layerInfo.layer.setOpacity(opacity);
}

// Helper to create vector style
function createVectorStyle(strokeColor, fillColor, fillOpacity = 0.5) {
    const strokeR = parseInt(strokeColor.slice(1, 3), 16);
    const strokeG = parseInt(strokeColor.slice(3, 5), 16);
    const strokeB = parseInt(strokeColor.slice(5, 7), 16);

    const fillR = parseInt(fillColor.slice(1, 3), 16);
    const fillG = parseInt(fillColor.slice(3, 5), 16);
    const fillB = parseInt(fillColor.slice(5, 7), 16);

    const strokeColorRGBA = `rgba(${strokeR}, ${strokeG}, ${strokeB}, 1)`;
    const fillColorRGBA = `rgba(${fillR}, ${fillG}, ${fillB}, ${fillOpacity})`;

    return new ol.style.Style({
        stroke: new ol.style.Stroke({
            color: strokeColorRGBA,
            width: 2
        }),
        fill: new ol.style.Fill({
            color: fillColorRGBA
        }),
        image: new ol.style.Circle({
            radius: 8,
            fill: new ol.style.Fill({
                color: strokeColorRGBA
            }),
            stroke: new ol.style.Stroke({
                color: 'rgba(255, 255, 255, 1)',
                width: 1
            })
        })
    });
}

// Create WebGL flat style for GPU-rendered vector layers
function createWebGLVectorStyle(strokeColor, fillColor, fillOpacity = 0.5) {
    const strokeR = parseInt(strokeColor.slice(1, 3), 16);
    const strokeG = parseInt(strokeColor.slice(3, 5), 16);
    const strokeB = parseInt(strokeColor.slice(5, 7), 16);

    const fillR = parseInt(fillColor.slice(1, 3), 16);
    const fillG = parseInt(fillColor.slice(3, 5), 16);
    const fillB = parseInt(fillColor.slice(5, 7), 16);

    return {
        'stroke-color': [strokeR, strokeG, strokeB, 1],
        'stroke-width': 2,
        'fill-color': [fillR, fillG, fillB, fillOpacity],
        'circle-radius': 8,
        'circle-fill-color': [strokeR, strokeG, strokeB, 1],
        'circle-stroke-color': [255, 255, 255, 1],
        'circle-stroke-width': 1,
    };
}

// Status helper
let _statusTimer = null;
function showStatus(message, isError = false) {
    const status = document.getElementById('status');
    status.textContent = message;
    status.style.display = '';
    status.className = 'status ' + (isError ? 'error' : 'success');

    // Also announce to screen readers for important messages
    if (isError) {
        announceToScreenReader(message, 'assertive');
    }

    if (_statusTimer) {
        clearTimeout(_statusTimer);
        _statusTimer = null;
    }

    if (!isError) {
        _statusTimer = setTimeout(() => {
            status.style.display = 'none';
            _statusTimer = null;
        }, 3000);
    }
}

// Compute min/max statistics from raster band data
function computeBandMinMax(bandData) {
    let min = Infinity, max = -Infinity;
    for (let i = 0; i < bandData.length; i++) {
        const v = bandData[i];
        if (isFinite(v)) {
            if (v < min) min = v;
            if (v > max) max = v;
        }
    }
    if (isFinite(min) && isFinite(max) && min !== max) {
        return { min, max };
    }
    return null;
}

// Compute statistics for a band from the smallest overview of a GeoTIFF
async function computeStatsFromTiff(tiff, image, sampleIndex) {
    try {
        const imageCount = await tiff.getImageCount();
        const sampleImage = imageCount > 1
            ? await tiff.getImage(imageCount - 1)
            : image;
        const rasters = await sampleImage.readRasters({ samples: [sampleIndex] });
        return computeBandMinMax(rasters[0]);
    } catch (e) {
        console.warn('Could not compute COG statistics:', e);
        return null;
    }
}

// Fetch COG header to get band count and data type info
async function getCOGBandInfo(url, sampleIndex) {
    try {
        const tiff = await GeoTIFF.fromUrl(url);
        const image = await tiff.getImage();
        const sampleFormat = image.fileDirectory.SampleFormat;
        const bitsPerSample = image.fileDirectory.BitsPerSample;
        const nodata = image.fileDirectory.GDAL_NODATA;
        const nodataVal = nodata !== undefined ? parseFloat(nodata) : undefined;
        const pi = image.fileDirectory.PhotometricInterpretation;
        const isRGB = pi === 2;
        const isFloat = sampleFormat && sampleFormat[0] === 3;

        let statistics = null;

        // For float data, compute statistics from the smallest overview so we can
        // set min/max for proper normalization stretch (RGB composites skip this
        // unless a specific band was requested)
        if (isFloat && (!isRGB || sampleIndex !== undefined)) {
            const statsIndex = sampleIndex !== undefined ? sampleIndex : 0;
            statistics = await computeStatsFromTiff(tiff, image, statsIndex);
        }

        return {
            bandCount: image.getSamplesPerPixel(),
            photometricInterpretation: pi,
            sampleFormat: sampleFormat ? sampleFormat[0] : 1, // 1=uint, 2=int, 3=float
            bitsPerSample: bitsPerSample ? bitsPerSample[0] : 8,
            nodata: nodataVal,
            statistics: statistics
        };
    } catch (e) {
        console.warn('Could not read COG band info, assuming defaults:', e);
        return { bandCount: 3, photometricInterpretation: 2, sampleFormat: 1, bitsPerSample: 8, nodata: undefined, statistics: null };
    }
}

// Compute statistics for a specific band (used when changing band selection)
async function getCOGBandStatistics(url, sampleIndex) {
    try {
        const tiff = await GeoTIFF.fromUrl(url);
        const image = await tiff.getImage();
        const sampleFormat = image.fileDirectory.SampleFormat;
        const isFloat = sampleFormat && sampleFormat[0] === 3;

        if (!isFloat) return null;

        return await computeStatsFromTiff(tiff, image, sampleIndex);
    } catch (e) {
        console.warn('Could not compute COG band statistics:', e);
        return null;
    }
}

// Build an ol.source.GeoTIFF with appropriate band selection and nodata handling
function createGeoTIFFSource(url, bandInfo, selectedBand) {
    const sourceConfig = {
        url: url,
        crossOrigin: 'anonymous',
        transition: 0
    };

    const isRGB = bandInfo.photometricInterpretation === 2;

    // Band selection: selectedBand > 0 renders that single band; 0/undefined means
    // the default (RGB composite for RGB data, first band otherwise)
    if (selectedBand > 0 && bandInfo.bandCount > 1) {
        sourceConfig.bands = [selectedBand];
    } else if (isRGB) {
        if (bandInfo.bandCount > 4) {
            sourceConfig.bands = [1, 2, 3];
        }
    } else if (bandInfo.bandCount > 1) {
        sourceConfig.bands = [1];
    }

    // For float scientific data with statistics, set min/max for proper stretch
    if (bandInfo.statistics) {
        sourceConfig.min = bandInfo.statistics.min;
        sourceConfig.max = bandInfo.statistics.max;
    }

    // Set nodata value — creates an alpha band for transparency
    if (bandInfo.nodata !== undefined) {
        sourceConfig.nodata = bandInfo.nodata;
    }

    return new ol.source.GeoTIFF({
        sources: [sourceConfig],
        convertToRGB: 'auto',
        interpolate: true,
        normalize: true
    });
}

// Create a WebGLTile layer with appropriate styling for the data type
function createCOGLayer(source, bandInfo, colormap) {
    const hasNodata = bandInfo.nodata !== undefined;
    const style = getColormapStyle(colormap, hasNodata);
    const opts = { source };
    if (style) opts.style = style;
    return new ol.layer.WebGLTile(opts);
}

// Load COG layer
async function loadCOG(url) {
    try {
        const bandInfo = await getCOGBandInfo(url);
        const source = createGeoTIFFSource(url, bandInfo);

        // Listen for errors on the source
        source.on('tileloaderror', function(event) {
            console.error('Tile load error:', event);
            showStatus('Error loading COG tiles. File may not be a valid Cloud Optimized GeoTIFF.', true);
        });

        // Create layer with appropriate styling for data type
        const layer = createCOGLayer(source, bandInfo);

        // Add to map
        map.addLayer(layer);
        addLayerToManager(layer, url, 'cog');
        const lastLayer = loadedLayers[loadedLayers.length - 1];
        lastLayer.hasNodata = bandInfo.nodata !== undefined;
        lastLayer.bandCount = bandInfo.bandCount;
        // 0 = RGB composite (default for RGB data); N >= 1 = single band N
        lastLayer.selectedBand = bandInfo.photometricInterpretation === 2 ? 0 : 1;
        lastLayer.bandInfo = bandInfo;

        showStatus('COG layer loaded successfully');
        document.getElementById('dataUrl').value = '';
    } catch (error) {
        showStatus('Error loading COG: ' + error.message, true);
        console.error('COG loading error:', error);
    }
}

function loadVectorTiles(url) {
    try {
        // Auto-detect format based on URL or use MVT as default
        // Supports MVT (Mapbox Vector Tiles) and other formats OpenLayers supports
        let format;

        // Check URL extension or use MVT as default
        if (url.includes('.pbf') || url.includes('.mvt')) {
            format = new ol.format.MVT();
        } else if (url.includes('.geojson')) {
            format = new ol.format.GeoJSON();
        } else {
            format = new ol.format.MVT();
        }

        // Create vector tile source
        const source = new ol.source.VectorTile({
            format: format,
            url: url,
            // Common tile URL pattern - adjust if needed
            // Expects {z}/{x}/{y} pattern in URL
            crossOrigin: 'anonymous'
        });

        // Get next color from palette
        const defaultColor = getNextVectorColor();

        // Create vector tile layer with default styling
        const layer = new ol.layer.VectorTile({
            source: source,
            style: createVectorStyle(defaultColor, defaultColor, 0.5)
        });

        // Add to map
        map.addLayer(layer);
        addLayerToManager(layer, url, 'vector', defaultColor, defaultColor);

        showStatus('Vector tiles layer loaded successfully');
        document.getElementById('dataUrl').value = '';
    } catch (error) {
        showStatus('Error loading vector tiles: ' + error.message, true);
        console.error('Vector tiles loading error:', error);
    }
}

// Check if a PMTiles archive contains raster tiles
// header.tileType: 0=Unknown, 1=MVT, 2=PNG, 3=JPEG, 4=WebP, 5=AVIF
function isPMTilesRaster(header) {
    return header.tileType >= 2 && header.tileType <= 5;
}

// Create a raster tile layer backed by a PMTiles archive (PNG/JPEG/WebP/AVIF tiles)
function createPMTilesRasterLayer(p, header, urlTemplate) {
    const source = new ol.source.XYZ({
        url: urlTemplate,
        minZoom: header.minZoom || 0,
        // OpenLayers upsamples tiles from maxZoom automatically when overzooming
        maxZoom: header.maxZoom || 14,
        tileLoadFunction: async (tile, src) => {
            const re = /(\d+)\/(\d+)\/(\d+)$/;
            const result = src.match(re);
            if (!result) return;
            const z = parseInt(result[1]);
            const x = parseInt(result[2]);
            const y = parseInt(result[3]);

            try {
                const response = await p.getZxy(z, x, y);
                if (response && response.data) {
                    const blob = new Blob([response.data]);
                    const objectUrl = URL.createObjectURL(blob);
                    const image = tile.getImage();
                    image.addEventListener('load', () => URL.revokeObjectURL(objectUrl), { once: true });
                    image.addEventListener('error', () => URL.revokeObjectURL(objectUrl), { once: true });
                    image.src = objectUrl;
                } else {
                    // No tile at this coordinate (outside archive coverage)
                    tile.setState(4); // TileState.EMPTY
                }
            } catch (err) {
                console.error('Error loading PMTiles raster tile:', err);
                tile.setState(3); // TileState.ERROR
            }
        }
    });

    return new ol.layer.Tile({
        source: source
    });
}

// Create a vector tile source backed by a PMTiles archive (MVT tiles)
function createPMTilesVectorSource(p, header, urlTemplate) {
    const mvtFormat = new ol.format.MVT();

    const source = new ol.source.VectorTile({
        format: mvtFormat,
        url: urlTemplate,
        // Set maxZoom to allow overzooming beyond the data's max zoom
        // OpenLayers will automatically reuse tiles from maxZoom when zooming beyond it
        maxZoom: header.maxZoom || 14,
        tileLoadFunction: async (tile, src) => {
            const re = /(\d+)\/(\d+)\/(\d+)$/;
            const result = src.match(re);
            if (result) {
                let z = parseInt(result[1]);
                const x = parseInt(result[2]);
                const y = parseInt(result[3]);

                // Clamp zoom level to the actual data's max zoom
                const dataMaxZoom = header.maxZoom || 14;
                const actualZ = Math.min(z, dataMaxZoom);

                try {
                    // If we're overzooming, calculate the parent tile coordinates
                    let tileX = x;
                    let tileY = y;
                    if (z > dataMaxZoom) {
                        const zoomDiff = z - dataMaxZoom;
                        tileX = Math.floor(x / Math.pow(2, zoomDiff));
                        tileY = Math.floor(y / Math.pow(2, zoomDiff));
                    }

                    const response = await p.getZxy(actualZ, tileX, tileY);
                    if (response && response.data) {
                        // Calculate tile extent for proper coordinate transformation
                        const tileGrid = source.getTileGrid();
                        const tileCoord = [z, x, y];
                        const extent = tileGrid.getTileCoordExtent(tileCoord);

                        const features = mvtFormat.readFeatures(response.data, {
                            extent: extent,
                            featureProjection: map.getView().getProjection()
                        });
                        tile.setFeatures(features);
                    } else {
                        tile.setFeatures([]);
                    }
                } catch (err) {
                    console.error('Error loading PMTiles tile:', err);
                    tile.setFeatures([]);
                }
            }
        }
    });

    return source;
}

// Load PMTiles layer
async function loadPMTiles(url) {
    try {
        // Use PMTiles library to read the archive
        const p = new pmtiles.PMTiles(url);

        // Get metadata to find bounds
        const header = await p.getHeader();

        // Raster PMTiles (PNG/JPEG/WebP/AVIF) render as image tiles
        if (isPMTilesRaster(header)) {
            const layer = createPMTilesRasterLayer(p, header, `pmtiles://${url}/{z}/{x}/{y}`);
            map.addLayer(layer);
            addLayerToManager(layer, url, 'pmtiles', null, null, null, { rasterTiles: true });
            showStatus('PMTiles raster layer loaded successfully');
            document.getElementById('dataUrl').value = '';
            return;
        }

        // Create vector tile source with custom tile loader
        const source = createPMTilesVectorSource(p, header, `pmtiles://${url}/{z}/{x}/{y}`);

        // Get next color from palette
        const defaultColor = getNextVectorColor();

        // Create vector tile layer with default styling
        const layer = new ol.layer.VectorTile({
            source: source,
            style: createVectorStyle(defaultColor, defaultColor, 0.5)
        });

        // Add to map
        map.addLayer(layer);
        addLayerToManager(layer, url, 'pmtiles', defaultColor, defaultColor);
        showStatus('PMTiles layer loaded successfully');
        document.getElementById('dataUrl').value = '';
    } catch (error) {
        showStatus('Error loading PMTiles: ' + error.message, true);
        console.error('PMTiles loading error:', error);
    }
}

// Load PMTiles from local file
async function loadPMTilesFromFile(file) {
    try {
        // Create FileSource for local file
        const fileSource = new pmtiles.FileSource(file);
        const p = new pmtiles.PMTiles(fileSource);

        // Get metadata to find bounds
        const header = await p.getHeader();

        // Raster PMTiles (PNG/JPEG/WebP/AVIF) render as image tiles
        if (isPMTilesRaster(header)) {
            const layer = createPMTilesRasterLayer(p, header, `pmtiles://local/${file.name}/{z}/{x}/{y}`);
            map.addLayer(layer);
            addLayerToManager(layer, null, 'pmtiles', null, null, file, { rasterTiles: true });
            showStatus(`PMTiles raster file loaded: ${file.name}`);
            return;
        }

        // Create vector tile source with custom tile loader
        const source = createPMTilesVectorSource(p, header, `pmtiles://local/${file.name}/{z}/{x}/{y}`);

        // Get next color from palette
        const defaultColor = getNextVectorColor();

        // Create vector tile layer with default styling
        const layer = new ol.layer.VectorTile({
            source: source,
            style: createVectorStyle(defaultColor, defaultColor, 0.5)
        });

        // Add to map
        map.addLayer(layer);
        addLayerToManager(layer, null, 'pmtiles', defaultColor, defaultColor, file);
        showStatus(`PMTiles file loaded: ${file.name}`);
    } catch (error) {
        showStatus('Error loading PMTiles file: ' + error.message, true);
        console.error('PMTiles file loading error:', error);
    }
}

// Load GeoJSON from local file
async function loadGeoJSONFromFile(file) {
    try {
        // Read the file as text
        const text = await file.text();
        const geojsonData = JSON.parse(text);

        // Detect source CRS (legacy `crs` member) and reproject if needed
        const dataProjection = await resolveGeoJSONProjection(geojsonData, file.name);

        // Create vector source from GeoJSON
        const source = new ol.source.Vector({
            features: new ol.format.GeoJSON().readFeatures(geojsonData, {
                dataProjection: dataProjection,
                featureProjection: map.getView().getProjection()
            })
        });

        // Get next color from palette
        const defaultColor = getNextVectorColor();

        // Create vector layer with default styling
        const layer = new ol.layer.Vector({
            source: source,
            style: createVectorStyle(defaultColor, defaultColor, 0.5)
        });

        // Add to map
        map.addLayer(layer);
        addLayerToManager(layer, null, 'geojson', defaultColor, defaultColor, file);

        showStatus(`GeoJSON file loaded: ${file.name}`);
    } catch (error) {
        showStatus('Error loading GeoJSON file: ' + error.message, true);
        console.error('GeoJSON file loading error:', error);
    }
}

// Load FlatGeobuf from local file
async function loadFlatGeobufFromFile(file) {
    try {
        // Check if flatgeobuf library is loaded
        if (typeof flatgeobuf === 'undefined') {
            throw new Error('FlatGeobuf library not loaded');
        }

        // Read file as ArrayBuffer
        const arrayBuffer = await file.arrayBuffer();

        // Create vector source
        const source = new ol.source.Vector({
            format: new ol.format.GeoJSON(),
            loader: async function(extent, resolution, projection) {
                try {
                    let headerCrs = null;

                    // Deserialize the FlatGeobuf from ArrayBuffer, capturing the header CRS.
                    // Unlike the stream variant, the Uint8Array variant returns a
                    // FeatureCollection directly rather than an async iterable.
                    const uint8Array = new Uint8Array(arrayBuffer);
                    const collection = flatgeobuf.deserialize(uint8Array, undefined, (header) => {
                        if (header.crs && header.crs.code) {
                            headerCrs = `${header.crs.org || 'EPSG'}:${header.crs.code}`;
                        }
                    });
                    const rawFeatures = collection.features || [];

                    // Reproject from the file's CRS to the map projection
                    const dataProjection = await resolveDetectedProjection(
                        headerCrs,
                        { type: 'FeatureCollection', features: rawFeatures },
                        file.name
                    );
                    const features = new ol.format.GeoJSON().readFeatures(
                        { type: 'FeatureCollection', features: rawFeatures },
                        { dataProjection: dataProjection, featureProjection: projection }
                    );

                    source.addFeatures(features);
                } catch (err) {
                    console.error('Error loading FlatGeobuf features:', err);
                    throw err;
                }
            }
        });

        // Get next color from palette
        const defaultColor = getNextVectorColor();

        // Create vector layer with default styling
        const layer = new ol.layer.Vector({
            source: source,
            style: createVectorStyle(defaultColor, defaultColor, 0.5)
        });

        // Add to map
        map.addLayer(layer);
        addLayerToManager(layer, null, 'flatgeobuf', defaultColor, defaultColor, file);
        showStatus(`FlatGeobuf file loaded: ${file.name}`);
    } catch (error) {
        showStatus('Error loading FlatGeobuf file: ' + error.message, true);
        console.error('FlatGeobuf file loading error:', error);
    }
}

// Load GeoJSON layer
async function loadGeoJSON(url) {
    try {
        // Fetch the GeoJSON data
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const geojsonData = await response.json();

        // Detect source CRS (legacy `crs` member) and reproject if needed
        const dataProjection = await resolveGeoJSONProjection(geojsonData, 'GeoJSON');

        // Create vector source from GeoJSON
        const source = new ol.source.Vector({
            features: new ol.format.GeoJSON().readFeatures(geojsonData, {
                dataProjection: dataProjection,
                featureProjection: map.getView().getProjection()
            })
        });

        // Get next color from palette
        const defaultColor = getNextVectorColor();

        // Create vector layer with default styling
        const layer = new ol.layer.Vector({
            source: source,
            style: createVectorStyle(defaultColor, defaultColor, 0.5)
        });

        // Add to map
        map.addLayer(layer);
        addLayerToManager(layer, url, 'geojson', defaultColor, defaultColor);

        showStatus('GeoJSON layer loaded successfully');
        document.getElementById('dataUrl').value = '';
    } catch (error) {
        showStatus('Error loading GeoJSON: ' + error.message, true);
        console.error('GeoJSON loading error:', error);
    }
}

// ===== GeoParquet Support =====

const GEOPARQUET_MAX_FEATURES = 50000;

function loadFzstd() {
    return new Promise((resolve, reject) => {
        if (window.fzstd) return resolve(window.fzstd);
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/fzstd@0.1.1/umd/index.js';
        script.onload = () => resolve(window.fzstd);
        script.onerror = () => reject(new Error('Failed to load fzstd library'));
        document.head.appendChild(script);
    });
}
let _hyparquet = null;
let _compressors = null;

async function loadHyparquet() {
    if (_hyparquet) return _hyparquet;
    const [hp, fzstd] = await Promise.all([
        import('https://cdn.jsdelivr.net/npm/hyparquet@1.25.1/+esm'),
        loadFzstd()
    ]);
    _hyparquet = hp;
    _compressors = {
        ZSTD: (input) => fzstd.decompress(input)
    };
    return _hyparquet;
}

// Convert hyparquet row objects to GeoJSON FeatureCollection
function rowsToGeoJSON(rows, geometryColumn) {
    const features = [];
    for (const row of rows) {
        const geometry = row[geometryColumn];
        if (!geometry) continue;
        const properties = {};
        for (const [key, value] of Object.entries(row)) {
            if (key !== geometryColumn) {
                properties[key] = value;
            }
        }
        features.push({
            type: 'Feature',
            geometry: geometry,
            properties: properties
        });
    }
    return { type: 'FeatureCollection', features };
}

// Get the geometry column name from GeoParquet metadata
function isGeoParquet(metadata) {
    if (metadata.key_value_metadata) {
        return metadata.key_value_metadata.some(kv => kv.key === 'geo');
    }
    return false;
}

function getGeoParquetGeometryColumn(metadata) {
    if (metadata.key_value_metadata) {
        for (const kv of metadata.key_value_metadata) {
            if (kv.key === 'geo') {
                try {
                    const geo = JSON.parse(kv.value);
                    return geo.primary_column || 'geometry';
                } catch (e) {
                    return 'geometry';
                }
            }
        }
    }
    return 'geometry';
}

// Get the CRS for a geometry column from GeoParquet "geo" metadata.
// The spec stores PROJJSON per column; absence means OGC:CRS84 (lon/lat).
function getGeoParquetCrs(metadata, geometryColumn) {
    if (!metadata.key_value_metadata) return null;
    for (const kv of metadata.key_value_metadata) {
        if (kv.key === 'geo') {
            try {
                const geo = JSON.parse(kv.value);
                return getProjjsonCrs(geo.columns?.[geometryColumn]?.crs);
            } catch (e) {
                return null;
            }
        }
    }
    return null;
}

// Load GeoParquet from URL
async function loadGeoParquet(url) {
    try {
        showStatus('Loading GeoParquet metadata...');
        const hp = await loadHyparquet();

        const file = await hp.asyncBufferFromUrl({ url });
        const metadata = await hp.parquetMetadataAsync(file);

        if (!isGeoParquet(metadata)) {
            throw new Error('This is a Parquet file but not a GeoParquet file. GeoParquet files require "geo" metadata with geometry column information.');
        }

        const totalRows = Number(metadata.num_rows);
        const geometryColumn = getGeoParquetGeometryColumn(metadata);

        const truncated = totalRows > GEOPARQUET_MAX_FEATURES;
        const readCount = Math.min(totalRows, GEOPARQUET_MAX_FEATURES);

        showStatus(`Reading ${readCount.toLocaleString()} of ${totalRows.toLocaleString()} features...`);

        const rows = await hp.parquetReadObjects({
            file,
            rowEnd: truncated ? GEOPARQUET_MAX_FEATURES : undefined,
            compressors: _compressors,
            geoparquet: true
        });

        const geojsonData = rowsToGeoJSON(rows, geometryColumn);

        if (geojsonData.features.length === 0) {
            throw new Error('No valid geometries found in GeoParquet file');
        }

        // Reproject from the file's CRS (PROJJSON in "geo" metadata) to the map projection
        const dataProjection = await resolveDetectedProjection(
            getGeoParquetCrs(metadata, geometryColumn), geojsonData, 'GeoParquet'
        );

        const source = new ol.source.Vector({
            features: new ol.format.GeoJSON().readFeatures(geojsonData, {
                dataProjection: dataProjection,
                featureProjection: map.getView().getProjection()
            })
        });

        const defaultColor = getNextVectorColor();

        const layer = new ol.layer.WebGLVector({
            source: source,
            style: createWebGLVectorStyle(defaultColor, defaultColor, 0.5)
        });

        map.addLayer(layer);
        addLayerToManager(layer, url, 'geoparquet', defaultColor, defaultColor);

        const msg = truncated
            ? `GeoParquet loaded (${readCount.toLocaleString()} of ${totalRows.toLocaleString()} features — zoom in for detail)`
            : `GeoParquet loaded (${totalRows.toLocaleString()} features)`;
        showStatus(msg);
        document.getElementById('dataUrl').value = '';
    } catch (error) {
        showStatus('Error loading GeoParquet: ' + error.message, true);
        console.error('GeoParquet loading error:', error);
    }
}

// Load GeoParquet from local file
async function loadGeoParquetFromFile(file) {
    try {
        showStatus('Loading GeoParquet file...');
        const hp = await loadHyparquet();

        const arrayBuffer = await file.arrayBuffer();
        const metadata = await hp.parquetMetadataAsync(arrayBuffer);

        if (!isGeoParquet(metadata)) {
            throw new Error('This is a Parquet file but not a GeoParquet file. GeoParquet files require "geo" metadata with geometry column information.');
        }

        const totalRows = Number(metadata.num_rows);
        const geometryColumn = getGeoParquetGeometryColumn(metadata);

        const truncated = totalRows > GEOPARQUET_MAX_FEATURES;
        const readCount = Math.min(totalRows, GEOPARQUET_MAX_FEATURES);

        showStatus(`Reading ${readCount.toLocaleString()} of ${totalRows.toLocaleString()} features...`);

        const rows = await hp.parquetReadObjects({
            file: arrayBuffer,
            rowEnd: truncated ? GEOPARQUET_MAX_FEATURES : undefined,
            compressors: _compressors,
            geoparquet: true
        });

        const geojsonData = rowsToGeoJSON(rows, geometryColumn);

        if (geojsonData.features.length === 0) {
            throw new Error('No valid geometries found in GeoParquet file');
        }

        // Reproject from the file's CRS (PROJJSON in "geo" metadata) to the map projection
        const dataProjection = await resolveDetectedProjection(
            getGeoParquetCrs(metadata, geometryColumn), geojsonData, file.name
        );

        const source = new ol.source.Vector({
            features: new ol.format.GeoJSON().readFeatures(geojsonData, {
                dataProjection: dataProjection,
                featureProjection: map.getView().getProjection()
            })
        });

        const defaultColor = getNextVectorColor();

        const layer = new ol.layer.WebGLVector({
            source: source,
            style: createWebGLVectorStyle(defaultColor, defaultColor, 0.5)
        });

        map.addLayer(layer);
        addLayerToManager(layer, null, 'geoparquet', defaultColor, defaultColor, file);

        const msg = truncated
            ? `GeoParquet file loaded: ${file.name} (${readCount.toLocaleString()} of ${totalRows.toLocaleString()} features)`
            : `GeoParquet file loaded: ${file.name} (${totalRows.toLocaleString()} features)`;
        showStatus(msg);
    } catch (error) {
        showStatus('Error loading GeoParquet file: ' + error.message, true);
        console.error('GeoParquet file loading error:', error);
    }
}

// Load FlatGeobuf layer
async function loadFlatGeobuf(url) {
    try {
        // Check if flatgeobuf library is loaded
        if (typeof flatgeobuf === 'undefined') {
            throw new Error('FlatGeobuf library not loaded');
        }

        // Create vector source
        const source = new ol.source.Vector({
            format: new ol.format.GeoJSON(),
            loader: async function(extent, resolution, projection) {
                try {
                    // Use FlatGeobuf's deserialize function to stream features
                    const response = await fetch(url);
                    const rawFeatures = [];
                    let headerCrs = null;

                    // Deserialize the FlatGeobuf stream, capturing the header CRS
                    for await (const feature of flatgeobuf.deserialize(response.body, undefined, (header) => {
                        if (header.crs && header.crs.code) {
                            headerCrs = `${header.crs.org || 'EPSG'}:${header.crs.code}`;
                        }
                    })) {
                        rawFeatures.push(feature);
                    }

                    // Reproject from the file's CRS to the map projection
                    const dataProjection = await resolveDetectedProjection(
                        headerCrs,
                        { type: 'FeatureCollection', features: rawFeatures },
                        'FlatGeobuf'
                    );
                    const features = new ol.format.GeoJSON().readFeatures(
                        { type: 'FeatureCollection', features: rawFeatures },
                        { dataProjection: dataProjection, featureProjection: projection }
                    );

                    source.addFeatures(features);
                } catch (err) {
                    console.error('Error loading FlatGeobuf features:', err);
                    throw err;
                }
            }
        });

        // Get next color from palette
        const defaultColor = getNextVectorColor();

        // Create vector layer with default styling
        const layer = new ol.layer.Vector({
            source: source,
            style: createVectorStyle(defaultColor, defaultColor, 0.5)
        });

        // Add to map
        map.addLayer(layer);
        addLayerToManager(layer, url, 'flatgeobuf', defaultColor, defaultColor);
        showStatus('FlatGeobuf layer loaded successfully');
        document.getElementById('dataUrl').value = '';
    } catch (error) {
        showStatus('Error loading FlatGeobuf: ' + error.message, true);
        console.error('FlatGeobuf loading error:', error);
    }
}

// Load Image Tiles layer (XYZ/TMS)
function loadImageTiles(url) {
    try {
        // Create image tile source
        const source = new ol.source.XYZ({
            url: url,
            crossOrigin: 'anonymous',
            // Optional: Add max zoom if needed
            // maxZoom: 19
        });

        // Create image tile layer
        const layer = new ol.layer.Tile({
            source: source
        });

        // Add to map
        map.addLayer(layer);
        addLayerToManager(layer, url, 'imagetiles');

        showStatus('Image tiles layer loaded successfully');
        document.getElementById('dataUrl').value = '';
    } catch (error) {
        showStatus('Error loading image tiles: ' + error.message, true);
        console.error('Image tiles loading error:', error);
    }
}

// ===== STAC Item Support =====

// Validate if data is a STAC Item
function isSTACItem(data) {
    return data &&
           data.type === 'Feature' &&
           data.stac_version &&
           data.assets &&
           data.geometry &&
           data.id &&
           data.properties;
}

// Supported STAC asset media types mapped to CloudMap loaders
const STAC_ASSET_TYPE_MAP = {
    'image/tiff; application=geotiff; profile=cloud-optimized': 'cog',
    'image/tiff; application=geotiff': 'cog',
    'image/tiff': 'cog',
    'application/geo+json': 'geojson',
    'application/json': 'geojson',
    'application/vnd.pmtiles': 'pmtiles',
    'application/pmtiles': 'pmtiles',
    'application/x-flatgeobuf': 'flatgeobuf',
    'application/flatgeobuf': 'flatgeobuf',
    'application/x-parquet': 'geoparquet',
    'application/vnd.apache.parquet': 'geoparquet',
    'application/geoparquet': 'geoparquet'
};

// Check if asset type is supported
function isSupportedAssetType(assetType) {
    if (!assetType) return false;
    // Normalize the type for comparison (handle case and extra params)
    const normalizedType = assetType.toLowerCase().split(';')[0].trim();
    for (const supportedType of Object.keys(STAC_ASSET_TYPE_MAP)) {
        if (supportedType.toLowerCase().startsWith(normalizedType) ||
            normalizedType.startsWith(supportedType.toLowerCase().split(';')[0])) {
            return true;
        }
    }
    return false;
}

// Get loader type for asset
function getLoaderTypeForAsset(asset) {
    if (!asset.type) {
        // Try to infer from href extension
        const href = asset.href.toLowerCase();
        if (href.match(/\.(tif|tiff|geotiff)$/)) return 'cog';
        if (href.match(/\.(geojson|json)$/)) return 'geojson';
        if (href.includes('.pmtiles')) return 'pmtiles';
        if (href.match(/\.(fgb|flatgeobuf)$/)) return 'flatgeobuf';
        return null;
    }

    const assetType = asset.type.toLowerCase();
    for (const [supportedType, loaderType] of Object.entries(STAC_ASSET_TYPE_MAP)) {
        if (assetType.includes(supportedType.toLowerCase().split(';')[0])) {
            return loaderType;
        }
    }
    return null;
}

// Resolve relative URLs against item's self link or base URL
function resolveSTACUrl(href, item, baseUrl) {
    if (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('s3://')) {
        return href;
    }
    // Try to find self link
    const selfLink = item.links?.find(l => l.rel === 'self')?.href;
    const base = selfLink || baseUrl;
    if (base) {
        try {
            return new URL(href, base).toString();
        } catch (e) {
            console.warn('Failed to resolve URL:', href, 'with base:', base);
        }
    }
    return href;
}

// Get loadable assets from STAC item
function getLoadableAssets(item, baseUrl) {
    const assets = [];
    for (const [key, asset] of Object.entries(item.assets)) {
        const loaderType = getLoaderTypeForAsset(asset);
        if (loaderType) {
            assets.push({
                key,
                title: asset.title || key,
                type: asset.type,
                roles: asset.roles || [],
                href: asset.href,
                resolvedHref: resolveSTACUrl(asset.href, item, baseUrl),
                loaderType
            });
        }
    }
    return assets;
}

// Select the best asset to auto-load
function selectBestAsset(assets) {
    // Priority: visual > rendered > overview > first COG
    const visualAsset = assets.find(a =>
        a.roles.includes('visual') || a.key === 'visual'
    );
    if (visualAsset) return visualAsset;

    const renderedAsset = assets.find(a =>
        a.roles.includes('rendered') || a.key === 'rendered'
    );
    if (renderedAsset) return renderedAsset;

    const overviewAsset = assets.find(a =>
        a.roles.includes('overview') || a.key === 'overview'
    );
    if (overviewAsset) return overviewAsset;

    // Return first COG
    const cogAsset = assets.find(a => a.loaderType === 'cog');
    if (cogAsset) return cogAsset;

    // Return first available asset
    return assets[0];
}

// Format datetime for display
function formatSTACDatetime(datetime) {
    if (!datetime) return null;
    try {
        const date = new Date(datetime);
        return date.toLocaleString();
    } catch {
        return datetime;
    }
}

// Load a specific STAC asset using the appropriate loader
async function loadSTACAssetDirect(asset, stacItem, stacItemUrl) {
    const { resolvedHref, loaderType, key, title } = asset;
    const layerName = `${stacItem.id} - ${title}`;

    // Store STAC metadata for later display
    const stacMetadata = {
        itemId: stacItem.id,
        stacItemUrl: stacItemUrl,
        assetKey: key,
        datetime: stacItem.properties.datetime,
        cloudCover: stacItem.properties['eo:cloud_cover'],
        platform: stacItem.properties.platform,
        constellation: stacItem.properties.constellation,
        instrument: stacItem.properties.instruments,
        gsd: stacItem.properties.gsd,
        properties: stacItem.properties,
        bbox: stacItem.bbox
    };

    try {
        let layer;
        let hasNodata = false;
        let bandInfo = null;
        let pmtilesRaster = false;

        if (loaderType === 'cog') {
            bandInfo = await getCOGBandInfo(resolvedHref);
            const source = createGeoTIFFSource(resolvedHref, bandInfo);
            hasNodata = bandInfo.nodata !== undefined;

            source.on('tileloaderror', function(event) {
                console.error('Tile load error:', event);
            });

            layer = createCOGLayer(source, bandInfo);

        } else if (loaderType === 'geojson') {
            const response = await fetch(resolvedHref);
            if (!response.ok) {
                throw new Error(`Failed to fetch GeoJSON: ${response.status}`);
            }
            const geojsonData = await response.json();
            const strokeColor = getNextVectorColor();
            const fillColor = strokeColor;

            const dataProjection = await resolveGeoJSONProjection(geojsonData, 'GeoJSON asset');

            const source = new ol.source.Vector({
                features: new ol.format.GeoJSON().readFeatures(geojsonData, {
                    dataProjection: dataProjection,
                    featureProjection: map.getView().getProjection()
                })
            });

            layer = new ol.layer.Vector({
                source: source,
                style: createVectorStyle(strokeColor, fillColor, 0.5)
            });

        } else if (loaderType === 'pmtiles') {
            // Use PMTiles loader - match the working loadPMTiles implementation
            const pmtilesInstance = new pmtiles.PMTiles(resolvedHref);
            const header = await pmtilesInstance.getHeader();

            // Raster PMTiles (PNG/JPEG/WebP/AVIF) render as image tiles
            if (isPMTilesRaster(header)) {
                pmtilesRaster = true;
                layer = createPMTilesRasterLayer(pmtilesInstance, header, `pmtiles://${resolvedHref}/{z}/{x}/{y}`);
            } else {
                const strokeColor = getNextVectorColor();
                const fillColor = strokeColor;

                const source = createPMTilesVectorSource(pmtilesInstance, header, `pmtiles://${resolvedHref}/{z}/{x}/{y}`);

                layer = new ol.layer.VectorTile({
                    source: source,
                    style: createVectorStyle(strokeColor, fillColor, 0.5)
                });
            }

        } else if (loaderType === 'flatgeobuf') {
            const strokeColor = getNextVectorColor();
            const fillColor = strokeColor;

            const source = new ol.source.Vector({
                loader: async function() {
                    try {
                        const response = await fetch(resolvedHref);
                        const rawFeatures = [];
                        let headerCrs = null;
                        for await (const feature of flatgeobuf.deserialize(response.body, undefined, (header) => {
                            if (header.crs && header.crs.code) {
                                headerCrs = `${header.crs.org || 'EPSG'}:${header.crs.code}`;
                            }
                        })) {
                            rawFeatures.push(feature);
                        }
                        const dataProjection = await resolveDetectedProjection(
                            headerCrs,
                            { type: 'FeatureCollection', features: rawFeatures },
                            'FlatGeobuf asset'
                        );
                        this.addFeatures(new ol.format.GeoJSON().readFeatures(
                            { type: 'FeatureCollection', features: rawFeatures },
                            { dataProjection: dataProjection, featureProjection: map.getView().getProjection() }
                        ));
                    } catch (e) {
                        console.error('FlatGeobuf error:', e);
                    }
                }
            });

            layer = new ol.layer.Vector({
                source: source,
                style: createVectorStyle(strokeColor, fillColor, 0.5)
            });
        }

        if (!layer) {
            throw new Error(`Unsupported loader type: ${loaderType}`);
        }

        map.addLayer(layer);

        // Add to layer manager with STAC metadata
        const name = layerName;
        const opacity = 1.0;
        const fillOpacity = 0.5;
        const isVector = ['geojson', 'pmtiles', 'flatgeobuf'].includes(loaderType) && !pmtilesRaster;

        // Get stroke/fill colors from the layer's style if it's a vector layer
        let strokeColor = '#007bff';
        let fillColor = '#007bff';
        if (isVector && layer.getStyle) {
            const style = layer.getStyle();
            if (typeof style === 'function') {
                // For style functions, extract color from a test call
                const testStyle = style(null, 1);
                if (testStyle) {
                    const stroke = testStyle.getStroke?.();
                    const fill = testStyle.getFill?.();
                    if (stroke) strokeColor = stroke.getColor() || strokeColor;
                    if (fill) {
                        const fillColorVal = fill.getColor();
                        if (Array.isArray(fillColorVal)) {
                            // Convert rgba array to hex
                            fillColor = `#${fillColorVal.slice(0, 3).map(c => c.toString(16).padStart(2, '0')).join('')}`;
                        } else {
                            fillColor = fillColorVal || fillColor;
                        }
                    }
                }
            }
        }

        loadedLayers.push({
            layer,
            url: resolvedHref,
            type: 'stac',
            name,
            strokeColor,
            fillColor,
            opacity,
            fillOpacity,
            colormap: 'none',
            hasNodata,
            isVector,
            rasterTiles: pmtilesRaster || undefined,
            stacMetadata,
            bandCount: bandInfo ? bandInfo.bandCount : undefined,
            selectedBand: bandInfo ? (bandInfo.photometricInterpretation === 2 ? 0 : 1) : undefined,
            bandInfo: bandInfo || undefined
        });

        updateLayerManager();
        announceToScreenReader(`Layer ${name} added`);

        // Auto-zoom to bbox if available
        if (stacItem.bbox && stacItem.bbox.length >= 4) {
            const extent = ol.proj.transformExtent(
                [stacItem.bbox[0], stacItem.bbox[1], stacItem.bbox[2], stacItem.bbox[3]],
                'EPSG:4326',
                map.getView().getProjection()
            );
            map.getView().fit(extent, { padding: [50, 50, 50, 50], maxZoom: 16 });
        }

        showStatus(`STAC asset "${title}" loaded successfully`);
        document.getElementById('dataUrl').value = '';

        return layer;
    } catch (error) {
        throw new Error(`Failed to load asset "${title}": ${error.message}`);
    }
}

// Main entry point for loading STAC Items
async function loadSTACItem(url) {
    try {
        showStatus('Loading STAC Item...');

        // Fetch the STAC Item
        const response = await fetch(url, {
            headers: {
                'Accept': 'application/json'
            }
        });

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error('STAC Item not found');
            }
            throw new Error(`Failed to fetch STAC Item: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        // Validate it's a STAC Item
        if (!isSTACItem(data)) {
            throw new Error('URL does not appear to be a valid STAC Item. Missing required fields (type, stac_version, assets, geometry, id, or properties).');
        }

        // Get loadable assets
        const loadableAssets = getLoadableAssets(data, url);

        if (loadableAssets.length === 0) {
            const assetTypes = Object.values(data.assets).map(a => a.type || 'unknown').join(', ');
            throw new Error(`No CloudMap-supported assets found in this STAC Item. Available asset types: ${assetTypes}`);
        }

        // Check if we should show asset selection modal or auto-select
        // Show modal if multiple visual-type assets exist
        const visualAssets = loadableAssets.filter(a =>
            a.roles.includes('visual') || a.roles.includes('rendered') ||
            a.roles.includes('overview') || a.key === 'visual'
        );

        if (loadableAssets.length > 1 && (visualAssets.length !== 1 || loadableAssets.length > 3)) {
            // Show asset selection modal
            showSTACAssetModal(data, loadableAssets, url);
        } else {
            // Auto-select best asset
            const bestAsset = selectBestAsset(loadableAssets);
            await loadSTACAssetDirect(bestAsset, data, url);
        }

    } catch (error) {
        showStatus('Error loading STAC Item: ' + error.message, true);
        console.error('STAC Item loading error:', error);
    }
}

// Show STAC asset selection modal
function showSTACAssetModal(stacItem, assets, stacItemUrl) {
    // Create or get modal
    let modal = document.getElementById('stacAssetModal');
    if (!modal) {
        modal = createSTACAssetModal();
    }

    // Populate modal
    const titleEl = modal.querySelector('.stac-modal-title');
    titleEl.textContent = `STAC Item: ${stacItem.id}`;

    const metaEl = modal.querySelector('.stac-modal-meta');
    let metaHtml = '';
    if (stacItem.properties.datetime) {
        metaHtml += `<div>Acquired: ${escapeHtml(formatSTACDatetime(stacItem.properties.datetime))}</div>`;
    }
    if (stacItem.properties['eo:cloud_cover'] !== undefined) {
        metaHtml += `<div>Cloud Cover: ${escapeHtml(String(stacItem.properties['eo:cloud_cover']))}%</div>`;
    }
    if (stacItem.properties.platform) {
        metaHtml += `<div>Platform: ${escapeHtml(stacItem.properties.platform)}</div>`;
    }
    metaEl.innerHTML = metaHtml || '<div>No metadata available</div>';

    const assetListEl = modal.querySelector('.stac-asset-list');
    assetListEl.innerHTML = '';

    assets.forEach((asset, index) => {
        const assetDiv = document.createElement('div');
        assetDiv.className = 'stac-asset-option';

        const radio = document.createElement('input');
        radio.type = 'radio';
        radio.name = 'stacAsset';
        radio.id = `stacAsset_${index}`;
        radio.value = index;
        if (index === 0) radio.checked = true;

        const label = document.createElement('label');
        label.htmlFor = `stacAsset_${index}`;

        const titleSpan = document.createElement('span');
        titleSpan.className = 'stac-asset-title';
        titleSpan.textContent = asset.title;

        const typeSpan = document.createElement('span');
        typeSpan.className = 'stac-asset-type';
        typeSpan.textContent = ` - ${asset.loaderType.toUpperCase()}`;

        if (asset.roles.length > 0) {
            const rolesSpan = document.createElement('span');
            rolesSpan.className = 'stac-asset-roles';
            rolesSpan.textContent = ` (${asset.roles.join(', ')})`;
            label.appendChild(titleSpan);
            label.appendChild(typeSpan);
            label.appendChild(rolesSpan);
        } else {
            label.appendChild(titleSpan);
            label.appendChild(typeSpan);
        }

        assetDiv.appendChild(radio);
        assetDiv.appendChild(label);
        assetListEl.appendChild(assetDiv);
    });

    // Store data for load button
    modal.dataset.stacItemUrl = stacItemUrl;
    modal._stacItem = stacItem;
    modal._assets = assets;

    // Show modal
    modal.style.display = 'flex';
    modal.setAttribute('aria-hidden', 'false');

    // Focus first radio
    const firstRadio = modal.querySelector('input[type="radio"]');
    if (firstRadio) firstRadio.focus();

    // Trap focus
    trapFocus(modal);
}

// Create STAC asset selection modal
function createSTACAssetModal() {
    const modal = document.createElement('div');
    modal.id = 'stacAssetModal';
    modal.className = 'modal-overlay';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'stacModalTitle');
    modal.innerHTML = `
        <div class="modal-content stac-modal">
            <div class="modal-header">
                <h3 id="stacModalTitle" class="stac-modal-title">STAC Item</h3>
                <button type="button" class="modal-close" aria-label="Close">&times;</button>
            </div>
            <div class="modal-body">
                <div class="stac-modal-meta"></div>
                <div class="stac-modal-section">
                    <label>Select asset to load:</label>
                    <div class="stac-asset-list" role="radiogroup" aria-label="Available assets"></div>
                </div>
            </div>
            <div class="modal-footer">
                <button type="button" class="modal-btn modal-btn-primary" id="stacLoadBtn">Load Asset</button>
                <button type="button" class="modal-btn" id="stacCancelBtn">Cancel</button>
            </div>
        </div>
    `;

    // Event listeners
    const closeBtn = modal.querySelector('.modal-close');
    const cancelBtn = modal.querySelector('#stacCancelBtn');
    const loadBtn = modal.querySelector('#stacLoadBtn');

    const closeModal = () => {
        modal.style.display = 'none';
        modal.setAttribute('aria-hidden', 'true');
    };

    closeBtn.addEventListener('click', closeModal);
    cancelBtn.addEventListener('click', closeModal);

    loadBtn.addEventListener('click', async () => {
        const selectedRadio = modal.querySelector('input[name="stacAsset"]:checked');
        if (!selectedRadio) return;

        const assetIndex = parseInt(selectedRadio.value);
        const asset = modal._assets[assetIndex];
        const stacItem = modal._stacItem;
        const stacItemUrl = modal.dataset.stacItemUrl;

        closeModal();

        try {
            await loadSTACAssetDirect(asset, stacItem, stacItemUrl);
        } catch (error) {
            showStatus('Error loading STAC asset: ' + error.message, true);
        }
    });

    // Close on Escape
    modal.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            closeModal();
        }
    });

    // Close on backdrop click - use mousedown/mouseup to avoid closing on resize release
    let mouseDownTarget = null;
    modal.addEventListener('mousedown', (e) => {
        mouseDownTarget = e.target;
    });
    modal.addEventListener('mouseup', (e) => {
        if (e.target === modal && mouseDownTarget === modal) {
            closeModal();
        }
        mouseDownTarget = null;
    });

    document.body.appendChild(modal);
    return modal;
}

// Show STAC metadata modal
function showSTACMetadataModal(stacMetadata) {
    // Create or get modal
    let modal = document.getElementById('stacMetadataModal');
    if (!modal) {
        modal = createSTACMetadataModal();
    }

    // Populate modal - use title or name from properties if available
    const titleEl = modal.querySelector('.stac-metadata-title');
    const itemTitle = stacMetadata.properties?.title || stacMetadata.properties?.name || stacMetadata.itemId || 'STAC Item Metadata';
    titleEl.textContent = itemTitle; // textContent is safe — no HTML injection

    const contentEl = modal.querySelector('.stac-metadata-content');
    let html = '<dl class="stac-metadata-list">';

    // Show item ID if title/name was used for the modal title
    if ((stacMetadata.properties?.title || stacMetadata.properties?.name) && stacMetadata.itemId) {
        html += `<dt>Item ID</dt><dd>${escapeHtml(stacMetadata.itemId)}</dd>`;
    }

    // Basic properties
    if (stacMetadata.datetime) {
        html += `<dt>Acquired</dt><dd>${escapeHtml(formatSTACDatetime(stacMetadata.datetime))}</dd>`;
    }

    if (stacMetadata.cloudCover !== undefined) {
        html += `<dt>Cloud Cover</dt><dd>${escapeHtml(String(stacMetadata.cloudCover))}%</dd>`;
    }

    if (stacMetadata.platform) {
        html += `<dt>Platform</dt><dd>${escapeHtml(stacMetadata.platform)}</dd>`;
    }

    if (stacMetadata.constellation) {
        html += `<dt>Constellation</dt><dd>${escapeHtml(stacMetadata.constellation)}</dd>`;
    }

    if (stacMetadata.instrument) {
        const instruments = Array.isArray(stacMetadata.instrument)
            ? stacMetadata.instrument.join(', ')
            : stacMetadata.instrument;
        html += `<dt>Instrument</dt><dd>${escapeHtml(instruments)}</dd>`;
    }

    if (stacMetadata.gsd !== undefined) {
        html += `<dt>Ground Sample Distance</dt><dd>${escapeHtml(String(stacMetadata.gsd))}m</dd>`;
    }

    if (stacMetadata.bbox) {
        const bbox = stacMetadata.bbox;
        html += `<dt>Bounding Box</dt><dd>${bbox[0].toFixed(4)}, ${bbox[1].toFixed(4)}, ${bbox[2].toFixed(4)}, ${bbox[3].toFixed(4)}</dd>`;
    }

    // Additional properties
    if (stacMetadata.properties) {
        const excludeKeys = ['datetime', 'eo:cloud_cover', 'platform', 'constellation', 'instruments', 'gsd', 'title', 'name'];
        const additionalProps = Object.entries(stacMetadata.properties)
            .filter(([key]) => !excludeKeys.includes(key))
            .filter(([_, value]) => value !== null && value !== undefined);

        if (additionalProps.length > 0) {
            html += '</dl><h4>Additional Properties</h4><dl class="stac-metadata-list">';
            for (const [key, value] of additionalProps) {
                const displayValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
                html += `<dt>${escapeHtml(key)}</dt><dd>${escapeHtml(displayValue)}</dd>`;
            }
        }
    }

    html += '</dl>';

    // Add link to STAC Item (only allow http/https URLs)
    if (stacMetadata.stacItemUrl && /^https?:\/\//i.test(stacMetadata.stacItemUrl)) {
        html += `<div class="stac-metadata-link"><a href="${escapeHtml(stacMetadata.stacItemUrl)}" target="_blank" rel="noopener noreferrer">View STAC Item JSON</a></div>`;
    }

    contentEl.innerHTML = html;

    // Show modal
    modal.style.display = 'flex';
    modal.setAttribute('aria-hidden', 'false');

    // Focus close button
    const closeBtn = modal.querySelector('.modal-close');
    if (closeBtn) closeBtn.focus();

    // Trap focus
    trapFocus(modal);
}

// Create STAC metadata modal
function createSTACMetadataModal() {
    const modal = document.createElement('div');
    modal.id = 'stacMetadataModal';
    modal.className = 'modal-overlay';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'stacMetadataTitle');
    modal.innerHTML = `
        <div class="modal-content stac-metadata-modal">
            <div class="modal-header">
                <h3 id="stacMetadataTitle" class="stac-metadata-title">STAC Item Metadata</h3>
                <button type="button" class="modal-close" aria-label="Close">&times;</button>
            </div>
            <div class="modal-body">
                <div class="stac-metadata-content"></div>
            </div>
            <div class="modal-footer">
                <button type="button" class="modal-btn" id="stacMetadataCloseBtn">Close</button>
            </div>
        </div>
    `;

    // Event listeners
    const closeBtn = modal.querySelector('.modal-close');
    const closeFooterBtn = modal.querySelector('#stacMetadataCloseBtn');
    const modalContent = modal.querySelector('.modal-content');
    const modalHeader = modal.querySelector('.modal-header');

    const closeModal = () => {
        modal.style.display = 'none';
        modal.setAttribute('aria-hidden', 'true');
    };

    closeBtn.addEventListener('click', closeModal);
    closeFooterBtn.addEventListener('click', closeModal);

    // Double-click header to toggle maximize
    let isMaximized = false;
    let previousSize = { width: '', height: '' };

    modalHeader.addEventListener('dblclick', (e) => {
        // Don't maximize if clicking on close button
        if (e.target.closest('.modal-close')) return;

        if (isMaximized) {
            // Restore previous size
            modalContent.style.width = previousSize.width;
            modalContent.style.height = previousSize.height;
            isMaximized = false;
        } else {
            // Save current size and maximize
            previousSize.width = modalContent.style.width || '';
            previousSize.height = modalContent.style.height || '';
            modalContent.style.width = '90vw';
            modalContent.style.height = '90vh';
            isMaximized = true;
        }
    });

    // Change cursor on header to indicate double-click action
    modalHeader.style.cursor = 'pointer';
    modalHeader.title = 'Double-click to maximize/restore';

    // Close on Escape
    modal.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            closeModal();
        }
    });

    // Close on backdrop click - use mousedown to avoid closing on resize release
    // Track where mousedown started to ensure both down and up are on backdrop
    let mouseDownTarget = null;
    modal.addEventListener('mousedown', (e) => {
        mouseDownTarget = e.target;
    });
    modal.addEventListener('mouseup', (e) => {
        if (e.target === modal && mouseDownTarget === modal) {
            closeModal();
        }
        mouseDownTarget = null;
    });

    document.body.appendChild(modal);
    return modal;
}

// ===== End STAC Item Support =====

// Check if a URL is a tile template ({z}/{x}/{y} in any order, path or query, TMS {-y} included)
function isTileTemplate(urlLower) {
    return urlLower.includes('{z}') && urlLower.includes('{x}') &&
           (urlLower.includes('{y}') || urlLower.includes('{-y}'));
}

// Strip query string and fragment so extension checks only see the path
// (e.g. a TiTiler URL like /tiles/{z}/{x}/{y}?url=data.tif is not a COG)
function urlPath(urlLower) {
    return urlLower.split(/[?#]/)[0];
}

// Positive evidence that a tile URL serves raster images
function hasImageTileHints(urlLower) {
    const pathLower = urlPath(urlLower);
    return /\.(png(8|32)?|jpe?g|webp|avif|gif)$/.test(pathLower) ||
           pathLower.includes('/mapserver/tile/') ||
           pathLower.includes('/wmts') ||
           /@[0-9]+(\.[0-9]+)?x/.test(pathLower);
}

// Positive evidence that a tile URL serves vector tiles
function hasVectorTileHints(urlLower) {
    const pathLower = urlPath(urlLower);
    return pathLower.includes('.pbf') || pathLower.includes('.mvt') ||
           /format=(pbf|mvt|application%2fvnd\.mapbox-vector-tile)/.test(urlLower);
}

// Substitute a concrete tile coordinate (and subdomain) into a tile URL template
function fillTileTemplate(url, z, x, y) {
    return url
        .replace(/\{z\}/gi, z)
        .replace(/\{-y\}/gi, Math.pow(2, z) - 1 - y)
        .replace(/\{x\}/gi, x)
        .replace(/\{y\}/gi, y)
        .replace(/\{s\}/gi, 'a');
}

// Load a URL in an <img> to test for raster content; works without CORS headers
function probeTileAsImage(sampleUrl) {
    return new Promise((resolve) => {
        const img = new Image();
        const timer = setTimeout(() => {
            img.src = '';
            resolve(false);
        }, 8000);
        img.onload = () => { clearTimeout(timer); resolve(true); };
        img.onerror = () => { clearTimeout(timer); resolve(false); };
        img.src = sampleUrl;
    });
}

// Identify what a tile template URL serves by fetching a sample tile and
// inspecting the Content-Type / magic bytes. Falls back to an <img> load test
// when fetch is blocked by CORS (image tile servers often omit CORS headers
// since <img> rendering doesn't need them). Returns 'imagetiles', 'vector',
// or null if inconclusive.
async function probeTileType(url) {
    // Sample a few zoom levels in case the service has a min zoom or
    // regional coverage; each candidate lies within world bounds
    const samples = [[0, 0, 0], [4, 8, 5], [8, 128, 90]];
    for (const [z, x, y] of samples) {
        const sampleUrl = fillTileTemplate(url, z, x, y);
        try {
            const resp = await fetch(sampleUrl);
            if (!resp.ok) continue;
            const contentType = (resp.headers.get('content-type') || '').toLowerCase();
            if (contentType.startsWith('image/')) return 'imagetiles';
            if (contentType.includes('protobuf') || contentType.includes('mapbox-vector')) return 'vector';
            if (contentType.includes('text/') || contentType.includes('json')) continue;
            // Ambiguous header (e.g. application/octet-stream): sniff magic bytes
            const bytes = new Uint8Array(await resp.arrayBuffer());
            if (bytes.length < 4) continue;
            const isPng = bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47;
            const isJpeg = bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
            const isGif = bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46;
            const isWebp = bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 &&
                           bytes.length >= 12 && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50;
            const isAvif = bytes.length >= 12 && bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70;
            if (isPng || isJpeg || isGif || isWebp || isAvif) return 'imagetiles';
            // Raw gzip or a protobuf layers-field tag (0x1a) both indicate MVT
            const isGzip = bytes[0] === 0x1f && bytes[1] === 0x8b;
            if (isGzip || bytes[0] === 0x1a) return 'vector';
        } catch (e) {
            // fetch blocked (CORS) or network error — an <img> can still load it
            if (await probeTileAsImage(sampleUrl)) return 'imagetiles';
        }
    }
    return null;
}

// Infer data type from URL
function inferDataType(url) {
    const urlLower = url.toLowerCase();
    const pathLower = urlPath(urlLower);

    // Check for PMTiles
    if (pathLower.includes('.pmtiles')) {
        return 'pmtiles';
    }
    // Check for COG (common extensions)
    else if (pathLower.match(/\.(tif|tiff|geotiff)$/)) {
        return 'cog';
    }
    // Check for GeoParquet
    else if (pathLower.match(/\.(parquet|geoparquet)$/)) {
        return 'geoparquet';
    }
    // Check for FlatGeobuf
    else if (pathLower.match(/\.(fgb|flatgeobuf)$/)) {
        return 'flatgeobuf';
    }
    // Check for STAC Item (before GeoJSON to take precedence)
    else if (pathLower.match(/\/collections\/[^/]+\/items\/[^/]+$/) ||
             pathLower.match(/\/item\.json$/) ||
             pathLower.match(/[\w-]+\.item\.json$/)) {
        return 'stac';
    }
    // Check for GeoJSON
    else if (pathLower.match(/\.(geojson|json)$/)) {
        return 'geojson';
    }
    // Check for vector tiles (MVT/PBF)
    else if (hasVectorTileHints(urlLower)) {
        return 'vector';
    }
    // Check for image tiles (tile template with raster evidence)
    else if (isTileTemplate(urlLower) && hasImageTileHints(urlLower)) {
        return 'imagetiles';
    }
    // A tile template with no format evidence is ambiguous — callers can
    // probe a sample tile with probeTileType() to resolve it

    return null;
}

// Validate data type matches URL
function validateDataType(url, selectedType) {
    const urlLower = url.toLowerCase();
    const pathLower = urlPath(urlLower);
    const warnings = [];

    // Check for PMTiles
    if (pathLower.includes('.pmtiles')) {
        if (selectedType !== 'pmtiles') {
            warnings.push(`URL appears to be PMTiles but "${selectedType}" is selected`);
        }
    }
    // Check for COG (common extensions)
    else if (pathLower.match(/\.(tif|tiff|geotiff)$/)) {
        if (selectedType !== 'cog') {
            warnings.push(`URL appears to be a GeoTIFF but "${selectedType}" is selected`);
        }
    }
    // Check for GeoParquet
    else if (pathLower.match(/\.(parquet|geoparquet)$/)) {
        if (selectedType !== 'geoparquet') {
            warnings.push(`URL appears to be GeoParquet but "${selectedType}" is selected`);
        }
    }
    // Check for FlatGeobuf
    else if (pathLower.match(/\.(fgb|flatgeobuf)$/)) {
        if (selectedType !== 'flatgeobuf') {
            warnings.push(`URL appears to be FlatGeobuf but "${selectedType}" is selected`);
        }
    }
    // Check for STAC Item (before GeoJSON)
    else if (pathLower.match(/\/collections\/[^/]+\/items\/[^/]+$/) ||
             pathLower.match(/\/item\.json$/) ||
             pathLower.match(/[\w-]+\.item\.json$/)) {
        if (selectedType !== 'stac') {
            warnings.push(`URL appears to be a STAC Item but "${selectedType}" is selected`);
        }
    }
    // Check for GeoJSON
    else if (pathLower.match(/\.(geojson|json)$/)) {
        if (selectedType !== 'geojson') {
            warnings.push(`URL appears to be GeoJSON but "${selectedType}" is selected`);
        }
    }
    // Check for vector tiles (MVT/PBF)
    else if (hasVectorTileHints(urlLower)) {
        if (selectedType !== 'vector') {
            warnings.push(`URL appears to be vector tiles but "${selectedType}" is selected`);
        }
    }
    // Check for image tiles (tile template with raster evidence)
    else if (isTileTemplate(urlLower) && hasImageTileHints(urlLower)) {
        if (selectedType !== 'imagetiles') {
            warnings.push(`URL appears to be image tiles but "${selectedType}" is selected`);
        }
    }
    // Generic tile URL pattern without clear type
    else if (isTileTemplate(urlLower)) {
        if (selectedType !== 'vector' && selectedType !== 'imagetiles') {
            warnings.push(`URL contains a tile pattern ({z}/{x}/{y}) but "${selectedType}" is selected`);
        }
    }

    return warnings;
}

// Event listeners
document.getElementById('loadBtn').addEventListener('click', function() {
    const dataType = document.getElementById('dataType').value;
    const dataUrl = document.getElementById('dataUrl').value.trim();

    if (!dataUrl) {
        showStatus('Please enter a URL', true);
        return;
    }

    // Validate URL (allow tile template URLs with {z}/{x}/{y} placeholders)
    try {
        // For tile URLs with placeholders, replace them temporarily for validation
        const urlToValidate = fillTileTemplate(dataUrl, 0, 0, 0);
        new URL(urlToValidate);
    } catch (e) {
        showStatus('Invalid URL format', true);
        return;
    }

    // Check if data type matches URL
    const warnings = validateDataType(dataUrl, dataType);
    if (warnings.length > 0) {
        const proceed = confirm(`Warning: ${warnings.join(', ')}. Continue anyway?`);
        if (!proceed) {
            return;
        }
    }

    // Check for duplicates
    const datasetKey = `${dataType}:${dataUrl}`;
    if (loadedDatasets.has(datasetKey)) {
        showStatus('This dataset is already loaded', true);
        return;
    }

    // Mark as loaded
    loadedDatasets.add(datasetKey);

    // Load based on type
    if (dataType === 'cog') {
        loadCOG(dataUrl);
    } else if (dataType === 'vector') {
        loadVectorTiles(dataUrl);
    } else if (dataType === 'pmtiles') {
        loadPMTiles(dataUrl);
    } else if (dataType === 'geojson') {
        loadGeoJSON(dataUrl);
    } else if (dataType === 'flatgeobuf') {
        loadFlatGeobuf(dataUrl);
    } else if (dataType === 'imagetiles') {
        loadImageTiles(dataUrl);
    } else if (dataType === 'geoparquet') {
        loadGeoParquet(dataUrl);
    } else if (dataType === 'stac') {
        loadSTACItem(dataUrl);
    }
});

// Allow loading on Enter key
document.getElementById('dataUrl').addEventListener('keypress', function(e) {
    if (e.key === 'Enter') {
        document.getElementById('loadBtn').click();
    }
});

// Auto-detect data type from URL. A manually chosen type is never overwritten;
// manual choice resets when the URL field is cleared.
let dataTypeManuallySelected = false;
let tileProbeToken = 0;

document.getElementById('dataType').addEventListener('change', function() {
    dataTypeManuallySelected = true;
});

document.getElementById('dataUrl').addEventListener('input', function(e) {
    const url = e.target.value.trim();
    const token = ++tileProbeToken; // invalidate any in-flight probe
    if (!url) {
        dataTypeManuallySelected = false;
        return;
    }
    if (dataTypeManuallySelected) {
        return;
    }
    const inferredType = inferDataType(url);
    if (inferredType) {
        document.getElementById('dataType').value = inferredType;
    } else if (isTileTemplate(url.toLowerCase())) {
        // Tile template without format evidence: probe a sample tile
        // (debounced so we don't fetch on every keystroke)
        setTimeout(() => {
            if (token !== tileProbeToken) return;
            probeTileType(url).then((type) => {
                if (type && token === tileProbeToken && !dataTypeManuallySelected &&
                    document.getElementById('dataUrl').value.trim() === url) {
                    document.getElementById('dataType').value = type;
                }
            });
        }, 600);
    }
});

// Custom attribution control
function updateAttribution() {
    const panel = document.getElementById('attributionPanel');
    const attributions = [];

    // Include nested basemap layers and pass the view for extent-based credits.
    map.getAllLayers().forEach(layer => {
        attributions.push(...layer.getAttributions(map.getView()));
    });

    // Update panel content
    if (attributions.length > 0) {
        panel.innerHTML = [...new Set(attributions)].join(' | ');
    } else {
        panel.innerHTML = '© <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors';
    }
}

// Toggle attribution panel
document.getElementById('attributionToggle').addEventListener('click', function() {
    const panel = document.getElementById('attributionPanel');
    panel.classList.toggle('visible');
    const isExpanded = panel.classList.contains('visible');
    this.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    this.setAttribute('aria-label', isExpanded ? 'Hide map attribution' : 'Show map attribution');
    if (isExpanded) {
        updateAttribution();
    }
});

// Close attribution panel when clicking outside
document.addEventListener('click', function(e) {
    const toggle = document.getElementById('attributionToggle');
    const panel = document.getElementById('attributionPanel');
    if (!toggle.contains(e.target) && !panel.contains(e.target)) {
        panel.classList.remove('visible');
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-label', 'Show map attribution');
    }
});

// Update attribution when layers change
map.getLayers().on('add', updateAttribution);
map.getLayers().on('remove', updateAttribution);

// Load from query parameters on initial load
function loadFromQueryParams() {
    const params = new URLSearchParams(window.location.search);
    const url = params.get('url');
    const type = params.get('type');

    if (url) {
        document.getElementById('dataUrl').value = url;

        if (type && ['cog', 'vector', 'pmtiles', 'geojson', 'flatgeobuf', 'geoparquet', 'imagetiles', 'stac'].includes(type)) {
            document.getElementById('dataType').value = type;
        }

        // Auto-load the data
        setTimeout(() => {
            document.getElementById('loadBtn').click();
        }, 500);
    }
}

// Controls collapse/expand
document.getElementById('controlsHeader').addEventListener('click', function() {
    const controls = document.getElementById('controls');
    controls.classList.toggle('collapsed');
    const isExpanded = !controls.classList.contains('collapsed');
    this.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
});

// Layer manager collapse/expand
document.getElementById('layerManagerHeader').addEventListener('click', function() {
    const layerManager = document.getElementById('layerManager');
    layerManager.classList.toggle('collapsed');
    const isExpanded = !layerManager.classList.contains('collapsed');
    this.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
});

// Share button event listener
document.getElementById('shareBtn').addEventListener('click', function() {
    if (loadedLayers.length === 0) {
        showStatus('No layers to share. Load some layers first.', true);
        return;
    }

    // Count local file layers
    const localFileLayers = loadedLayers.filter(info => info.file);
    const urlBasedLayers = loadedLayers.filter(info => !info.file);

    if (localFileLayers.length > 0 && urlBasedLayers.length === 0) {
        showStatus('Cannot share: All layers are local files. Load URL-based layers to share.', true);
        return;
    }

    copyShareLink();

    // Show warning if some layers are excluded
    if (localFileLayers.length > 0) {
        const excludedNames = localFileLayers.map(l => l.file.name).join(', ');
        const message = localFileLayers.length === 1
            ? `Note: Local file "${excludedNames}" excluded from share link.`
            : `Note: ${localFileLayers.length} local files excluded from share link: ${excludedNames}`;
        setTimeout(() => {
            showStatus(message, false);
        }, 2100);
    }
});

// Check for hash state or query params on page load
window.addEventListener('DOMContentLoaded', async function() {
    // Try to load from hash first (shared link)
    const hasHash = await loadStateFromHash();

    // If no hash state, fall back to query params
    if (!hasHash) {
        loadFromQueryParams();
    }
});

// Dark mode logo switcher
function updateLogo() {
    const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const logoImg = document.querySelector('.reference img');
    if (logoImg) {
        logoImg.src = isDarkMode ? 'logo.png' : 'logo_dark.png';
    }
}

// Set logo on initial load
updateLogo();

// Listen for changes in color scheme
window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', updateLogo);

// ===== URL State Sharing =====

// Serialize map state as gzip-compressed UTF-8 JSON and URL-safe base64.
async function serializeMapState() {
    const view = map.getView();
    const center = ol.proj.toLonLat(view.getCenter());
    const zoom = view.getZoom();

    // Filter out local file layers and map the rest
    const layers = loadedLayers
        .filter(layerInfo => !layerInfo.file)  // Exclude local files
        .map(layerInfo => {
        const layerData = {
            url: layerInfo.url,
            type: layerInfo.type,
            o: layerInfo.opacity,
            v: layerInfo.layer.getVisible()
        };

        // Add custom name if it differs from default
        const defaultName = getLayerName(layerInfo.url, layerInfo.type);
        if (layerInfo.name !== defaultName) {
            layerData.name = layerInfo.name;
        }

        // Add colors for vector layers (raster PMTiles have no style to save)
        if (['vector', 'pmtiles', 'geojson', 'flatgeobuf', 'geoparquet'].includes(layerInfo.type) && !layerInfo.rasterTiles) {
            layerData.sc = layerInfo.strokeColor;
            layerData.fc = layerInfo.fillColor;
            layerData.fo = layerInfo.fillOpacity !== undefined ? layerInfo.fillOpacity : 0.5;
        }

        // Add STAC metadata for share links
        if (layerInfo.type === 'stac' && layerInfo.stacMetadata) {
            layerData.stacItemUrl = layerInfo.stacMetadata.stacItemUrl;
            layerData.assetKey = layerInfo.stacMetadata.assetKey;
        }

        // Add colormap and band selection for COG/STAC raster layers
        if (['cog', 'stac'].includes(layerInfo.type) && layerInfo.colormap && layerInfo.colormap !== 'none') {
            layerData.cm = layerInfo.colormap;
        }
        if (['cog', 'stac'].includes(layerInfo.type) && layerInfo.selectedBand !== undefined) {
            const isRGB = layerInfo.bandInfo && layerInfo.bandInfo.photometricInterpretation === 2;
            const defaultBand = isRGB ? 0 : 1;
            if (layerInfo.selectedBand !== defaultBand) {
                layerData.bn = layerInfo.selectedBand;
            }
        }

        return layerData;
    });

    const state = {
        v: [center[0], center[1], zoom],
        l: layers
    };

    const json = JSON.stringify(state);
    // Keep sharing available in browsers without Compression Streams support.
    if (typeof CompressionStream === 'undefined' || typeof DecompressionStream === 'undefined') {
        return btoa(encodeURIComponent(json));
    }
    const stream = new Blob([json]).stream().pipeThrough(new CompressionStream('gzip'));
    const bytes = new Uint8Array(await new Response(stream).arrayBuffer());
    // Avoid spreading a potentially large payload into function arguments.
    const binary = Array.from(bytes, byte => String.fromCharCode(byte)).join('');
    return 'v2:' + btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// Read both compressed v2 links and the original uncompressed links.
async function deserializeMapState(hash) {
    try {
        if (hash.startsWith('v2:')) {
            const base64 = hash.slice(3).replace(/-/g, '+').replace(/_/g, '/');
            const bytes = Uint8Array.from(atob(base64), char => char.charCodeAt(0));
            const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'));
            return JSON.parse(await new Response(stream).text());
        }
        const json = decodeURIComponent(atob(hash));
        return JSON.parse(json);
    } catch (e) {
        console.error('Error deserializing map state:', e);
        return null;
    }
}

// Generate shareable link
async function generateShareLink() {
    const hash = await serializeMapState();
    const url = new URL(window.location.href);
    url.hash = hash;
    // Remove query params to avoid conflicts
    url.search = '';
    return url.toString();
}

// Copy share link to clipboard
async function copyShareLink() {
    try {
        const shareLinkPromise = generateShareLink();
        const shareBtn = document.getElementById('shareBtn');
        const originalText = shareBtn.textContent;

        // Try modern clipboard API first
        if (navigator.clipboard && navigator.clipboard.write && typeof ClipboardItem !== 'undefined') {
            // Start the clipboard operation during the click gesture, before
            // async compression finishes (required by browsers such as Safari).
            await navigator.clipboard.write([new ClipboardItem({
                'text/plain': shareLinkPromise.then(link => new Blob([link], { type: 'text/plain' }))
            })]);
        } else if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(await shareLinkPromise);
        } else {
            const shareLink = await shareLinkPromise;
            // Fallback for older browsers or non-HTTPS contexts
            const textArea = document.createElement('textarea');
            textArea.value = shareLink;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
        }

        // Update button to show success
        shareBtn.textContent = 'Link Copied!';
        shareBtn.style.background = '#20c997';
        showStatus('Share link copied to clipboard!');

        // Reset button after 2 seconds
        setTimeout(() => {
            shareBtn.textContent = originalText;
            shareBtn.style.background = '';
        }, 2000);

        return true;
    } catch (err) {
        console.error('Failed to copy share link:', err);
        showStatus('Failed to copy share link', true);
        return false;
    }
}

// Load map state from URL hash
async function loadStateFromHash() {
    const hash = window.location.hash.substring(1); // Remove '#' prefix
    if (!hash) return false;

    const state = await deserializeMapState(hash);
    if (!state) return false;

    try {
        // Restore view (center and zoom)
        if (state.v && Array.isArray(state.v) && state.v.length === 3) {
            const [lon, lat, zoom] = state.v;
            map.getView().setCenter(ol.proj.fromLonLat([lon, lat]));
            map.getView().setZoom(zoom);
        }

        // Restore layers (in order)
        if (state.l && Array.isArray(state.l)) {
            for (const layerData of state.l) {
                // Check for duplicates
                const datasetKey = `${layerData.type}:${layerData.url}`;
                if (loadedDatasets.has(datasetKey)) {
                    console.log('Skipping duplicate layer:', layerData.url);
                    continue;
                }

                // Mark as loaded
                loadedDatasets.add(datasetKey);

                // Load the layer based on type
                if (layerData.type === 'cog') {
                    await loadCOGFromState(layerData);
                } else if (layerData.type === 'vector') {
                    loadVectorTilesFromState(layerData);
                } else if (layerData.type === 'pmtiles') {
                    await loadPMTilesFromState(layerData);
                } else if (layerData.type === 'geojson') {
                    await loadGeoJSONFromState(layerData);
                } else if (layerData.type === 'flatgeobuf') {
                    await loadFlatGeobufFromState(layerData);
                } else if (layerData.type === 'imagetiles') {
                    loadImageTilesFromState(layerData);
                } else if (layerData.type === 'geoparquet') {
                    await loadGeoParquetFromState(layerData);
                } else if (layerData.type === 'stac') {
                    await loadSTACFromState(layerData);
                }
            }
        }

        showStatus('Map state loaded from shared link');
        return true;
    } catch (error) {
        console.error('Error loading state from hash:', error);
        showStatus('Error loading shared map state', true);
        return false;
    }
}

// Helper functions to load layers with state preservation
async function loadCOGFromState(layerData) {
    try {
        const bandInfo = await getCOGBandInfo(layerData.url, layerData.bn > 0 ? layerData.bn - 1 : undefined);
        const selectedBand = layerData.bn !== undefined ? layerData.bn
            : (bandInfo.photometricInterpretation === 2 ? 0 : 1);
        const source = createGeoTIFFSource(layerData.url, bandInfo, selectedBand);

        source.on('tileloaderror', function(event) {
            console.error('Tile load error:', event);
        });

        const layer = createCOGLayer(source, bandInfo, layerData.cm);
        layer.setOpacity(layerData.o !== undefined ? layerData.o : 1.0);
        layer.setVisible(layerData.v !== undefined ? layerData.v : true);

        map.addLayer(layer);

        const name = layerData.name || getLayerName(layerData.url, layerData.type);
        loadedLayers.push({
            layer: layer,
            url: layerData.url,
            type: layerData.type,
            name: name,
            strokeColor: layerData.sc || '#007bff',
            fillColor: layerData.fc || '#007bff',
            opacity: layerData.o !== undefined ? layerData.o : 1.0,
            colormap: layerData.cm || 'none',
            hasNodata: bandInfo.nodata !== undefined,
            bandCount: bandInfo.bandCount,
            selectedBand: selectedBand,
            bandInfo: bandInfo
        });
        updateLayerManager();
    } catch (error) {
        console.error('Error loading COG from state:', error);
    }
}

function loadVectorTilesFromState(layerData) {
    try {
        let format;
        const url = layerData.url;
        if (url.includes('.pbf') || url.includes('.mvt')) {
            format = new ol.format.MVT();
        } else if (url.includes('.geojson')) {
            format = new ol.format.GeoJSON();
        } else {
            format = new ol.format.MVT();
        }

        const source = new ol.source.VectorTile({
            format: format,
            url: url,
            crossOrigin: 'anonymous'
        });

        const strokeColor = layerData.sc || '#007bff';
        const fillColor = layerData.fc || '#007bff';
        const fillOpacity = layerData.fo !== undefined ? layerData.fo : 0.5;

        const layer = new ol.layer.VectorTile({
            source: source,
            style: createVectorStyle(strokeColor, fillColor, fillOpacity)
        });

        layer.setOpacity(layerData.o !== undefined ? layerData.o : 1.0);
        layer.setVisible(layerData.v !== undefined ? layerData.v : true);

        map.addLayer(layer);

        const name = layerData.name || getLayerName(layerData.url, layerData.type);
        loadedLayers.push({
            layer: layer,
            url: layerData.url,
            type: layerData.type,
            name: name,
            strokeColor: strokeColor,
            fillColor: fillColor,
            fillOpacity: fillOpacity,
            opacity: layerData.o !== undefined ? layerData.o : 1.0
        });
        updateLayerManager();
    } catch (error) {
        console.error('Error loading vector tiles from state:', error);
    }
}

async function loadPMTilesFromState(layerData) {
    try {
        const p = new pmtiles.PMTiles(layerData.url);
        const header = await p.getHeader();

        // Raster PMTiles (PNG/JPEG/WebP/AVIF) render as image tiles
        if (isPMTilesRaster(header)) {
            const layer = createPMTilesRasterLayer(p, header, `pmtiles://${layerData.url}/{z}/{x}/{y}`);
            layer.setOpacity(layerData.o !== undefined ? layerData.o : 1.0);
            layer.setVisible(layerData.v !== undefined ? layerData.v : true);

            map.addLayer(layer);

            const name = layerData.name || getLayerName(layerData.url, layerData.type);
            loadedLayers.push({
                layer: layer,
                url: layerData.url,
                type: layerData.type,
                name: name,
                strokeColor: '#007bff',
                fillColor: '#007bff',
                opacity: layerData.o !== undefined ? layerData.o : 1.0,
                rasterTiles: true
            });
            updateLayerManager();
            return;
        }

        const source = createPMTilesVectorSource(p, header, `pmtiles://${layerData.url}/{z}/{x}/{y}`);

        const strokeColor = layerData.sc || '#007bff';
        const fillColor = layerData.fc || '#007bff';
        const fillOpacity = layerData.fo !== undefined ? layerData.fo : 0.5;

        const layer = new ol.layer.VectorTile({
            source: source,
            style: createVectorStyle(strokeColor, fillColor, fillOpacity)
        });

        layer.setOpacity(layerData.o !== undefined ? layerData.o : 1.0);
        layer.setVisible(layerData.v !== undefined ? layerData.v : true);

        map.addLayer(layer);

        const name = layerData.name || getLayerName(layerData.url, layerData.type);
        loadedLayers.push({
            layer: layer,
            url: layerData.url,
            type: layerData.type,
            name: name,
            strokeColor: strokeColor,
            fillColor: fillColor,
            fillOpacity: fillOpacity,
            opacity: layerData.o !== undefined ? layerData.o : 1.0
        });
        updateLayerManager();
    } catch (error) {
        console.error('Error loading PMTiles from state:', error);
    }
}

async function loadGeoJSONFromState(layerData) {
    try {
        const response = await fetch(layerData.url);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const geojsonData = await response.json();

        const dataProjection = await resolveGeoJSONProjection(geojsonData, 'GeoJSON');

        const source = new ol.source.Vector({
            features: new ol.format.GeoJSON().readFeatures(geojsonData, {
                dataProjection: dataProjection,
                featureProjection: map.getView().getProjection()
            })
        });

        const strokeColor = layerData.sc || '#007bff';
        const fillColor = layerData.fc || '#007bff';
        const fillOpacity = layerData.fo !== undefined ? layerData.fo : 0.5;

        const layer = new ol.layer.Vector({
            source: source,
            style: createVectorStyle(strokeColor, fillColor, fillOpacity)
        });

        layer.setOpacity(layerData.o !== undefined ? layerData.o : 1.0);
        layer.setVisible(layerData.v !== undefined ? layerData.v : true);

        map.addLayer(layer);

        const name = layerData.name || getLayerName(layerData.url, layerData.type);
        loadedLayers.push({
            layer: layer,
            url: layerData.url,
            type: layerData.type,
            name: name,
            strokeColor: strokeColor,
            fillColor: fillColor,
            fillOpacity: fillOpacity,
            opacity: layerData.o !== undefined ? layerData.o : 1.0
        });
        updateLayerManager();
    } catch (error) {
        console.error('Error loading GeoJSON from state:', error);
    }
}

async function loadFlatGeobufFromState(layerData) {
    try {
        if (typeof flatgeobuf === 'undefined') {
            throw new Error('FlatGeobuf library not loaded');
        }

        const source = new ol.source.Vector({
            format: new ol.format.GeoJSON(),
            loader: async function(extent, resolution, projection) {
                try {
                    const response = await fetch(layerData.url);
                    const rawFeatures = [];
                    let headerCrs = null;

                    for await (const feature of flatgeobuf.deserialize(response.body, undefined, (header) => {
                        if (header.crs && header.crs.code) {
                            headerCrs = `${header.crs.org || 'EPSG'}:${header.crs.code}`;
                        }
                    })) {
                        rawFeatures.push(feature);
                    }

                    const dataProjection = await resolveDetectedProjection(
                        headerCrs,
                        { type: 'FeatureCollection', features: rawFeatures },
                        'FlatGeobuf'
                    );
                    const features = new ol.format.GeoJSON().readFeatures(
                        { type: 'FeatureCollection', features: rawFeatures },
                        { dataProjection: dataProjection, featureProjection: projection }
                    );

                    source.addFeatures(features);
                } catch (err) {
                    console.error('Error loading FlatGeobuf features:', err);
                    throw err;
                }
            }
        });

        const strokeColor = layerData.sc || '#007bff';
        const fillColor = layerData.fc || '#007bff';
        const fillOpacity = layerData.fo !== undefined ? layerData.fo : 0.5;

        const layer = new ol.layer.Vector({
            source: source,
            style: createVectorStyle(strokeColor, fillColor, fillOpacity)
        });

        layer.setOpacity(layerData.o !== undefined ? layerData.o : 1.0);
        layer.setVisible(layerData.v !== undefined ? layerData.v : true);

        map.addLayer(layer);

        const name = layerData.name || getLayerName(layerData.url, layerData.type);
        loadedLayers.push({
            layer: layer,
            url: layerData.url,
            type: layerData.type,
            name: name,
            strokeColor: strokeColor,
            fillColor: fillColor,
            fillOpacity: fillOpacity,
            opacity: layerData.o !== undefined ? layerData.o : 1.0
        });
        updateLayerManager();
    } catch (error) {
        console.error('Error loading FlatGeobuf from state:', error);
    }
}

async function loadGeoParquetFromState(layerData) {
    try {
        const hp = await loadHyparquet();
        const file = await hp.asyncBufferFromUrl({ url: layerData.url });
        const metadata = await hp.parquetMetadataAsync(file);

        if (!isGeoParquet(metadata)) {
            throw new Error('This is a Parquet file but not a GeoParquet file. GeoParquet files require "geo" metadata with geometry column information.');
        }

        const totalRows = Number(metadata.num_rows);
        const geometryColumn = getGeoParquetGeometryColumn(metadata);

        const rows = await hp.parquetReadObjects({
            file,
            rowEnd: totalRows > GEOPARQUET_MAX_FEATURES ? GEOPARQUET_MAX_FEATURES : undefined,
            geoparquet: true
        });

        const geojsonData = rowsToGeoJSON(rows, geometryColumn);

        const dataProjection = await resolveDetectedProjection(
            getGeoParquetCrs(metadata, geometryColumn), geojsonData, 'GeoParquet'
        );

        const source = new ol.source.Vector({
            features: new ol.format.GeoJSON().readFeatures(geojsonData, {
                dataProjection: dataProjection,
                featureProjection: map.getView().getProjection()
            })
        });

        const strokeColor = layerData.sc || '#007bff';
        const fillColor = layerData.fc || '#007bff';
        const fillOpacity = layerData.fo !== undefined ? layerData.fo : 0.5;

        const layer = new ol.layer.WebGLVector({
            source: source,
            style: createWebGLVectorStyle(strokeColor, fillColor, fillOpacity)
        });

        layer.setOpacity(layerData.o !== undefined ? layerData.o : 1.0);
        layer.setVisible(layerData.v !== undefined ? layerData.v : true);

        map.addLayer(layer);

        const name = layerData.name || getLayerName(layerData.url, layerData.type);
        loadedLayers.push({
            layer: layer,
            url: layerData.url,
            type: layerData.type,
            name: name,
            strokeColor: strokeColor,
            fillColor: fillColor,
            fillOpacity: fillOpacity,
            opacity: layerData.o !== undefined ? layerData.o : 1.0
        });
        updateLayerManager();
    } catch (error) {
        console.error('Error loading GeoParquet from state:', error);
    }
}

function loadImageTilesFromState(layerData) {
    try {
        const source = new ol.source.XYZ({
            url: layerData.url,
            crossOrigin: 'anonymous'
        });

        const layer = new ol.layer.Tile({
            source: source
        });

        layer.setOpacity(layerData.o !== undefined ? layerData.o : 1.0);
        layer.setVisible(layerData.v !== undefined ? layerData.v : true);

        map.addLayer(layer);

        const name = layerData.name || getLayerName(layerData.url, layerData.type);
        loadedLayers.push({
            layer: layer,
            url: layerData.url,
            type: layerData.type,
            name: name,
            strokeColor: '#007bff',
            fillColor: '#007bff',
            opacity: layerData.o !== undefined ? layerData.o : 1.0
        });
        updateLayerManager();
    } catch (error) {
        console.error('Error loading image tiles from state:', error);
    }
}

// Load STAC layer from state
async function loadSTACFromState(layerData) {
    try {
        // If we have the asset URL directly, load it as a COG (most common case)
        // Otherwise, refetch the STAC item to find the asset
        if (layerData.stacItemUrl && layerData.assetKey) {
            // Fetch the STAC item to get the asset
            const response = await fetch(layerData.stacItemUrl);
            if (!response.ok) {
                throw new Error(`Failed to fetch STAC Item: ${response.status}`);
            }
            const stacItem = await response.json();

            if (!isSTACItem(stacItem)) {
                throw new Error('Invalid STAC Item');
            }

            const asset = stacItem.assets[layerData.assetKey];
            if (!asset) {
                throw new Error(`Asset "${layerData.assetKey}" not found in STAC Item`);
            }

            const loaderType = getLoaderTypeForAsset(asset);
            const resolvedHref = resolveSTACUrl(asset.href, stacItem, layerData.stacItemUrl);

            // Load based on asset type
            let selectedBand = layerData.bn;
            let layer;
            let hasNodata = false;
            let bandInfo = null;
            let pmtilesRaster = false;
            let isVector = ['geojson', 'pmtiles', 'flatgeobuf'].includes(loaderType);
            const strokeColor = layerData.sc || '#007bff';
            const fillColor = layerData.fc || '#007bff';
            const fillOpacity = layerData.fo !== undefined ? layerData.fo : 0.5;

            if (loaderType === 'cog') {
                bandInfo = await getCOGBandInfo(resolvedHref, selectedBand > 0 ? selectedBand - 1 : undefined);
                if (selectedBand === undefined) {
                    selectedBand = bandInfo.photometricInterpretation === 2 ? 0 : 1;
                }
                const source = createGeoTIFFSource(resolvedHref, bandInfo, selectedBand);
                hasNodata = bandInfo.nodata !== undefined;

                layer = createCOGLayer(source, bandInfo, layerData.cm);
            } else if (loaderType === 'geojson') {
                const resp = await fetch(resolvedHref);
                if (!resp.ok) throw new Error(`Failed to fetch GeoJSON: ${resp.status}`);
                const geojsonData = await resp.json();
                const dataProjection = await resolveGeoJSONProjection(geojsonData, 'GeoJSON asset');
                const source = new ol.source.Vector({
                    features: new ol.format.GeoJSON().readFeatures(geojsonData, {
                        dataProjection: dataProjection,
                        featureProjection: map.getView().getProjection()
                    })
                });
                layer = new ol.layer.Vector({
                    source: source,
                    style: createVectorStyle(strokeColor, fillColor, fillOpacity)
                });
            } else if (loaderType === 'flatgeobuf') {
                const source = new ol.source.Vector({
                    loader: async function() {
                        try {
                            const resp = await fetch(resolvedHref);
                            const rawFeatures = [];
                            let headerCrs = null;
                            for await (const feature of flatgeobuf.deserialize(resp.body, undefined, (header) => {
                                if (header.crs && header.crs.code) {
                                    headerCrs = `${header.crs.org || 'EPSG'}:${header.crs.code}`;
                                }
                            })) {
                                rawFeatures.push(feature);
                            }
                            const dataProjection = await resolveDetectedProjection(
                                headerCrs,
                                { type: 'FeatureCollection', features: rawFeatures },
                                'FlatGeobuf asset'
                            );
                            this.addFeatures(new ol.format.GeoJSON().readFeatures(
                                { type: 'FeatureCollection', features: rawFeatures },
                                { dataProjection: dataProjection, featureProjection: map.getView().getProjection() }
                            ));
                        } catch (e) {
                            console.error('FlatGeobuf error:', e);
                        }
                    }
                });
                layer = new ol.layer.Vector({
                    source: source,
                    style: createVectorStyle(strokeColor, fillColor, fillOpacity)
                });
            } else if (loaderType === 'pmtiles') {
                const pmtilesInstance = new pmtiles.PMTiles(resolvedHref);
                const header = await pmtilesInstance.getHeader();

                // Raster PMTiles (PNG/JPEG/WebP/AVIF) render as image tiles
                if (isPMTilesRaster(header)) {
                    pmtilesRaster = true;
                    isVector = false;
                    layer = createPMTilesRasterLayer(pmtilesInstance, header, `pmtiles://${resolvedHref}/{z}/{x}/{y}`);
                } else {
                    const source = createPMTilesVectorSource(pmtilesInstance, header, `pmtiles://${resolvedHref}/{z}/{x}/{y}`);
                    layer = new ol.layer.VectorTile({
                        source: source,
                        style: createVectorStyle(strokeColor, fillColor, fillOpacity)
                    });
                }
            } else {
                // Default fallback: assume COG
                bandInfo = await getCOGBandInfo(resolvedHref, selectedBand > 0 ? selectedBand - 1 : undefined);
                if (selectedBand === undefined) {
                    selectedBand = bandInfo.photometricInterpretation === 2 ? 0 : 1;
                }
                const source = createGeoTIFFSource(resolvedHref, bandInfo, selectedBand);
                hasNodata = bandInfo.nodata !== undefined;

                layer = createCOGLayer(source, bandInfo, layerData.cm);
            }

            layer.setOpacity(layerData.o !== undefined ? layerData.o : 1.0);
            layer.setVisible(layerData.v !== undefined ? layerData.v : true);

            map.addLayer(layer);

            const name = layerData.name || `${stacItem.id} - ${asset.title || layerData.assetKey}`;
            const stacMetadata = {
                itemId: stacItem.id,
                stacItemUrl: layerData.stacItemUrl,
                assetKey: layerData.assetKey,
                datetime: stacItem.properties.datetime,
                cloudCover: stacItem.properties['eo:cloud_cover'],
                platform: stacItem.properties.platform,
                constellation: stacItem.properties.constellation,
                instrument: stacItem.properties.instruments,
                gsd: stacItem.properties.gsd,
                properties: stacItem.properties,
                bbox: stacItem.bbox
            };

            loadedLayers.push({
                layer: layer,
                url: resolvedHref,
                type: 'stac',
                name: name,
                strokeColor: strokeColor,
                fillColor: fillColor,
                opacity: layerData.o !== undefined ? layerData.o : 1.0,
                fillOpacity: fillOpacity,
                colormap: layerData.cm || 'none',
                hasNodata,
                isVector,
                rasterTiles: pmtilesRaster || undefined,
                stacMetadata,
                bandCount: bandInfo ? bandInfo.bandCount : undefined,
                selectedBand: selectedBand,
                bandInfo: bandInfo || undefined
            });
            updateLayerManager();
        } else {
            // Fallback: load directly from the URL (should be asset URL)
            const bandInfo = await getCOGBandInfo(layerData.url, layerData.bn > 0 ? layerData.bn - 1 : undefined);
            const selectedBand = layerData.bn !== undefined ? layerData.bn
                : (bandInfo.photometricInterpretation === 2 ? 0 : 1);
            const source = createGeoTIFFSource(layerData.url, bandInfo, selectedBand);

            const layer = createCOGLayer(source, bandInfo, layerData.cm);
            layer.setOpacity(layerData.o !== undefined ? layerData.o : 1.0);
            layer.setVisible(layerData.v !== undefined ? layerData.v : true);

            map.addLayer(layer);

            const name = layerData.name || getLayerName(layerData.url, 'stac');
            loadedLayers.push({
                layer: layer,
                url: layerData.url,
                type: 'stac',
                name: name,
                strokeColor: '#007bff',
                fillColor: '#007bff',
                opacity: layerData.o !== undefined ? layerData.o : 1.0,
                fillOpacity: 0.5,
                colormap: layerData.cm || 'none',
                hasNodata: bandInfo.nodata !== undefined,
                bandCount: bandInfo.bandCount,
                selectedBand: selectedBand,
                bandInfo: bandInfo
            });
            updateLayerManager();
        }
    } catch (error) {
        console.error('Error loading STAC from state:', error);
        showStatus(`Error loading STAC layer: ${error.message}`, true);
    }
}

// ===== End URL State Sharing =====

// ===== Layer Edit Modal =====

let currentEditingLayer = null;
let modalFocusTrapCleanup = null;
let lastFocusedElement = null;

function openLayerEditModal(layerInfo) {
    currentEditingLayer = layerInfo;
    lastFocusedElement = document.activeElement;
    const modal = document.getElementById('layerEditModal');
    const modalContent = modal.querySelector('.modal-content');
    const isVectorLayer = (['vector', 'pmtiles', 'geojson', 'flatgeobuf', 'geoparquet'].includes(layerInfo.type) || layerInfo.isVector) && !layerInfo.rasterTiles;

    // Populate modal fields
    document.getElementById('modalLayerName').value = layerInfo.name;
    document.getElementById('modalLayerOpacity').value = layerInfo.opacity || 1.0;
    document.getElementById('modalLayerOpacityValue').textContent = `${Math.round((layerInfo.opacity || 1.0) * 100)}%`;

    // Update slider ARIA attributes
    const layerOpacitySlider = document.getElementById('modalLayerOpacity');
    const opacityPercent = Math.round((layerInfo.opacity || 1.0) * 100);
    layerOpacitySlider.setAttribute('aria-valuenow', opacityPercent);
    layerOpacitySlider.setAttribute('aria-valuetext', `${opacityPercent}%`);

    // Show/hide vector controls
    const vectorControls = document.getElementById('modalVectorControls');
    if (isVectorLayer) {
        vectorControls.style.display = 'block';
        document.getElementById('modalStrokeColor').value = layerInfo.strokeColor || '#007bff';
        document.getElementById('modalStrokeColorLabel').textContent = layerInfo.strokeColor || '#007bff';
        document.getElementById('modalFillColor').value = layerInfo.fillColor || '#007bff';
        document.getElementById('modalFillColorLabel').textContent = layerInfo.fillColor || '#007bff';
        document.getElementById('modalFillOpacity').value = layerInfo.fillOpacity !== undefined ? layerInfo.fillOpacity : 0.5;
        const fillOpacityPercent = Math.round((layerInfo.fillOpacity !== undefined ? layerInfo.fillOpacity : 0.5) * 100);
        document.getElementById('modalFillOpacityValue').textContent = `${fillOpacityPercent}%`;

        // Update fill opacity slider ARIA
        const fillOpacitySlider = document.getElementById('modalFillOpacity');
        fillOpacitySlider.setAttribute('aria-valuenow', fillOpacityPercent);
        fillOpacitySlider.setAttribute('aria-valuetext', `${fillOpacityPercent}%`);
    } else {
        vectorControls.style.display = 'none';
    }

    // Show/hide colormap controls for COG/STAC raster layers
    const colormapControls = document.getElementById('modalColormapControls');
    const isCOGLayer = layerInfo.type === 'cog' || (layerInfo.type === 'stac' && !layerInfo.isVector && !layerInfo.rasterTiles);
    if (isCOGLayer) {
        colormapControls.style.display = 'block';
        document.getElementById('modalColormap').value = layerInfo.colormap || 'none';

        // Show band selector for multi-band COGs; RGB files get a composite option first
        const bandControls = document.getElementById('modalBandControls');
        const bandSelect = document.getElementById('modalBand');
        const isRGB = layerInfo.bandInfo && layerInfo.bandInfo.photometricInterpretation === 2;
        if (layerInfo.bandCount > 1) {
            bandControls.style.display = 'block';
            bandSelect.innerHTML = '';
            if (isRGB) {
                const opt = document.createElement('option');
                opt.value = 0;
                opt.textContent = 'RGB (composite)';
                bandSelect.appendChild(opt);
            }
            for (let i = 1; i <= layerInfo.bandCount; i++) {
                const opt = document.createElement('option');
                opt.value = i;
                opt.textContent = 'Band ' + i;
                bandSelect.appendChild(opt);
            }
            bandSelect.value = layerInfo.selectedBand !== undefined
                ? layerInfo.selectedBand
                : (isRGB ? 0 : 1);
        } else {
            bandControls.style.display = 'none';
        }
    } else {
        colormapControls.style.display = 'none';
    }

    modal.classList.add('visible');

    // Focus first input and trap focus
    setTimeout(() => {
        document.getElementById('modalLayerName').focus();
        modalFocusTrapCleanup = trapFocus(modalContent);
    }, 50);
}

function closeLayerEditModal() {
    const modal = document.getElementById('layerEditModal');
    modal.classList.remove('visible');
    currentEditingLayer = null;

    // Clean up focus trap
    if (modalFocusTrapCleanup) {
        modalFocusTrapCleanup();
        modalFocusTrapCleanup = null;
    }

    // Return focus to triggering element
    if (lastFocusedElement) {
        lastFocusedElement.focus();
        lastFocusedElement = null;
    }
}

async function saveLayerEdits() {
    if (!currentEditingLayer) return;

    const isVectorLayer = (['vector', 'pmtiles', 'geojson', 'flatgeobuf', 'geoparquet'].includes(currentEditingLayer.type) || currentEditingLayer.isVector) && !currentEditingLayer.rasterTiles;

    // Save layer name
    const newName = document.getElementById('modalLayerName').value.trim();
    if (newName) {
        currentEditingLayer.name = newName;
    }

    // Save layer opacity
    const newOpacity = parseFloat(document.getElementById('modalLayerOpacity').value);
    updateLayerOpacity(currentEditingLayer, newOpacity);

    // Save vector layer settings
    if (isVectorLayer) {
        currentEditingLayer.strokeColor = document.getElementById('modalStrokeColor').value;
        currentEditingLayer.fillColor = document.getElementById('modalFillColor').value;
        currentEditingLayer.fillOpacity = parseFloat(document.getElementById('modalFillOpacity').value);
        updateLayerStyle(currentEditingLayer);
    }

    // Save colormap and band selection for COG/STAC raster layers
    const isCOGLayer = currentEditingLayer.type === 'cog' || (currentEditingLayer.type === 'stac' && !currentEditingLayer.isVector && !currentEditingLayer.rasterTiles);
    if (isCOGLayer) {
        const newColormap = document.getElementById('modalColormap').value;
        currentEditingLayer.colormap = newColormap;

        // Check if band changed (0 = RGB composite)
        const bandControls = document.getElementById('modalBandControls');
        if (bandControls.style.display !== 'none') {
            const newBand = parseInt(document.getElementById('modalBand').value);
            const isRGB = currentEditingLayer.bandInfo && currentEditingLayer.bandInfo.photometricInterpretation === 2;
            const oldBand = currentEditingLayer.selectedBand !== undefined
                ? currentEditingLayer.selectedBand
                : (isRGB ? 0 : 1);

            if (newBand !== oldBand) {
                currentEditingLayer.selectedBand = newBand;

                // Recompute statistics for the new band if float data
                const bandInfo = { ...currentEditingLayer.bandInfo };
                if (bandInfo.sampleFormat === 3 && newBand > 0) {
                    showStatus('Computing statistics for band ' + newBand + '...');
                    const stats = await getCOGBandStatistics(currentEditingLayer.url, newBand - 1);
                    bandInfo.statistics = stats;
                    currentEditingLayer.bandInfo = bandInfo;
                } else if (newBand === 0) {
                    // Back to RGB composite: drop single-band statistics
                    bandInfo.statistics = null;
                    currentEditingLayer.bandInfo = bandInfo;
                }

                // Recreate source with the new band
                const source = createGeoTIFFSource(currentEditingLayer.url, bandInfo, newBand);
                currentEditingLayer.layer.setSource(source);
            }
        }

        // setStyle(undefined) throws in OL 10; {} restores default rendering
        const style = getColormapStyle(newColormap, currentEditingLayer.hasNodata);
        currentEditingLayer.layer.setStyle(style || {});
    }

    updateLayerManager();
    closeLayerEditModal();
}

// Modal event listeners
document.getElementById('modalClose').addEventListener('click', closeLayerEditModal);
document.getElementById('modalCancel').addEventListener('click', closeLayerEditModal);
document.getElementById('modalSave').addEventListener('click', saveLayerEdits);

// Close modal on overlay click
document.getElementById('layerEditModal').addEventListener('click', (e) => {
    if (e.target.id === 'layerEditModal') {
        closeLayerEditModal();
    }
});

// Close modal on Escape key
document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
        const modal = document.getElementById('layerEditModal');
        if (modal.classList.contains('visible')) {
            closeLayerEditModal();
        }
    }
});

// Update color labels when color changes
document.getElementById('modalStrokeColor').addEventListener('input', (e) => {
    document.getElementById('modalStrokeColorLabel').textContent = e.target.value;
});

document.getElementById('modalFillColor').addEventListener('input', (e) => {
    document.getElementById('modalFillColorLabel').textContent = e.target.value;
});

// Update opacity labels when sliders change
document.getElementById('modalFillOpacity').addEventListener('input', (e) => {
    const value = Math.round(parseFloat(e.target.value) * 100);
    document.getElementById('modalFillOpacityValue').textContent = `${value}%`;
    e.target.setAttribute('aria-valuenow', value);
    e.target.setAttribute('aria-valuetext', `${value}%`);
});

document.getElementById('modalLayerOpacity').addEventListener('input', (e) => {
    const value = Math.round(parseFloat(e.target.value) * 100);
    document.getElementById('modalLayerOpacityValue').textContent = `${value}%`;
    e.target.setAttribute('aria-valuenow', value);
    e.target.setAttribute('aria-valuetext', `${value}%`);
});

// ===== End Layer Edit Modal =====

// ===== Feature Click Handler =====

// Store features found at click location
let clickedFeatures = [];
let currentFeatureIndex = 0;
let lastClickCoordinate = null;
let identifyClickToken = 0;

// ===== COG Pixel Identify =====

// COG/STAC raster layers that can be queried for pixel values
function isIdentifiableRasterLayer(info) {
    return (info.type === 'cog' || (info.type === 'stac' && !info.isVector && !info.rasterTiles))
        && info.layer.getVisible();
}

// Lazily open the GeoTIFF and gather what identify needs (projection, extent,
// per-band scale/offset). Cached as a promise on the layer.
function getIdentifyContext(layerInfo) {
    if (!layerInfo.identifyCache) {
        layerInfo.identifyCache = (async () => {
            const tiff = await GeoTIFF.fromUrl(layerInfo.url);
            const image = await tiff.getImage();

            // Prefer the projection OL resolved for rendering; fall back to geokeys
            let projection = null;
            try {
                const viewOptions = await layerInfo.layer.getSource().getView();
                projection = viewOptions.projection;
            } catch (e) {
                const epsg = image.geoKeys && (image.geoKeys.ProjectedCSTypeGeoKey || image.geoKeys.GeographicTypeGeoKey);
                if (epsg && epsg !== 32767) {
                    projection = await ensureProjection('EPSG:' + epsg);
                }
            }

            const bandCount = image.getSamplesPerPixel();
            const bandScales = [];
            for (let i = 0; i < bandCount; i++) {
                let scale = 1, offset = 0;
                try {
                    const md = image.getGDALMetadata(i);
                    if (md) {
                        if (md.SCALE !== undefined) scale = parseFloat(md.SCALE);
                        if (md.OFFSET !== undefined) offset = parseFloat(md.OFFSET);
                    }
                } catch (e) { /* no per-band metadata */ }
                bandScales.push({ scale, offset });
            }

            return {
                image,
                projection,
                bandScales,
                nodata: image.getGDALNoData(),
                bbox: image.getBoundingBox(),
                width: image.getWidth(),
                height: image.getHeight()
            };
        })();
        // Allow retry on transient failures instead of caching the rejection
        layerInfo.identifyCache.catch(() => { layerInfo.identifyCache = null; });
    }
    return layerInfo.identifyCache;
}

function formatPixelNumber(v) {
    if (!isFinite(v)) return String(v);
    if (Number.isInteger(v)) return String(v);
    return String(parseFloat(v.toPrecision(6)));
}

// Read raw values for every band at the clicked coordinate. Returns a popup
// entry, or null when the click is outside this layer (or on error).
async function identifyCOGPixel(layerInfo, coordinate) {
    try {
        const ctx = await getIdentifyContext(layerInfo);
        if (!ctx.projection) return null;

        const imgCoord = ol.proj.transform(coordinate, map.getView().getProjection(), ctx.projection);
        const [minX, minY, maxX, maxY] = ctx.bbox;
        if (imgCoord[0] < minX || imgCoord[0] > maxX || imgCoord[1] < minY || imgCoord[1] > maxY) {
            return null;
        }

        const px = Math.min(ctx.width - 1, Math.floor((imgCoord[0] - minX) / (maxX - minX) * ctx.width));
        const py = Math.min(ctx.height - 1, Math.floor((maxY - imgCoord[1]) / (maxY - minY) * ctx.height));
        const rasters = await ctx.image.readRasters({ window: [px, py, px + 1, py + 1] });

        const values = {};
        for (let i = 0; i < rasters.length; i++) {
            const raw = rasters[i][0];
            const { scale, offset } = ctx.bandScales[i] || { scale: 1, offset: 0 };
            let display;
            if (ctx.nodata !== null && ctx.nodata !== undefined && raw === ctx.nodata) {
                display = 'nodata';
            } else if (scale !== 1 || offset !== 0) {
                display = `${formatPixelNumber(raw * scale + offset)} (raw: ${formatPixelNumber(raw)})`;
            } else {
                display = formatPixelNumber(raw);
            }
            values['Band ' + (i + 1)] = display;
        }
        return { isRaster: true, layerInfo, values };
    } catch (e) {
        console.warn('Could not identify pixel for', layerInfo.name, e);
        return null;
    }
}

// Add click handler to map for feature inspection
map.on('click', function(evt) {
    const pixel = evt.pixel;
    const coordinate = evt.coordinate;

    // Collect all features at this pixel
    clickedFeatures = [];

    // Check all layers for features at this pixel
    map.forEachFeatureAtPixel(pixel, function(feature, layer) {
        if (layer) {
            // Only collect from vector layers (skip raster/COG layers)
            const layerInfo = loadedLayers.find(info => info.layer === layer);
            const isVectorLayer = layerInfo && (['vector', 'pmtiles', 'geojson', 'flatgeobuf', 'geoparquet'].includes(layerInfo.type) || layerInfo.isVector);
            if (isVectorLayer) {
                clickedFeatures.push({
                    feature: feature,
                    layer: layer,
                    layerInfo: layerInfo
                });
            }
        }
    });

    // Raster layers are queried asynchronously; topmost first
    const rasterLayers = loadedLayers.filter(isIdentifiableRasterLayer).reverse();
    const token = ++identifyClickToken;

    // Show vector features right away (or a placeholder while raster reads run)
    if (clickedFeatures.length > 0) {
        currentFeatureIndex = 0;
        lastClickCoordinate = coordinate;
        showFeaturePopup(clickedFeatures[0], coordinate);
    } else if (rasterLayers.length === 0) {
        hideFeaturePopup();
        return;
    } else {
        const popup = document.getElementById('featurePopup');
        document.querySelector('.feature-popup-header h4').textContent = 'Identifying...';
        document.getElementById('featurePopupBody').innerHTML =
            '<div class="feature-popup-empty">Reading pixel values...</div>';
        const clickPixel = map.getPixelFromCoordinate(coordinate);
        popup.style.left = `${clickPixel[0] + 10}px`;
        popup.style.top = `${clickPixel[1] + 10}px`;
        popup.classList.add('visible');
    }

    Promise.all(rasterLayers.map(info => identifyCOGPixel(info, coordinate))).then(results => {
        if (token !== identifyClickToken) return; // superseded by a newer click
        const hits = results.filter(Boolean);
        if (hits.length === 0) {
            if (clickedFeatures.length === 0) hideFeaturePopup();
            return;
        }
        const hadVector = clickedFeatures.length > 0;
        clickedFeatures = clickedFeatures.concat(hits);
        if (!hadVector) {
            currentFeatureIndex = 0;
            lastClickCoordinate = coordinate;
        }
        showFeaturePopup(clickedFeatures[currentFeatureIndex], hadVector ? null : coordinate);
    });
});

// Change cursor on hover over features (throttled for performance)
let lastPointerMoveTime = 0;
const pointerMoveThrottle = 50; // milliseconds

map.on('pointermove', function(evt) {
    if (evt.dragging) {
        return;
    }

    // Throttle to avoid excessive feature checks
    const now = Date.now();
    if (now - lastPointerMoveTime < pointerMoveThrottle) {
        return;
    }
    lastPointerMoveTime = now;

    const pixel = evt.pixel;
    let hasFeature = false;

    map.forEachFeatureAtPixel(pixel, function(feature, layer) {
        if (layer) {
            const layerInfo = loadedLayers.find(info => info.layer === layer);
            const isVectorLayer = layerInfo && (['vector', 'pmtiles', 'geojson', 'flatgeobuf', 'geoparquet'].includes(layerInfo.type) || layerInfo.isVector);
            if (isVectorLayer) {
                hasFeature = true;
                return true;
            }
        }
    });

    // COG/STAC raster layers are identifiable wherever they have rendered data
    if (!hasFeature) {
        hasFeature = loadedLayers.some(info => {
            if (!isIdentifiableRasterLayer(info)) return false;
            try {
                return !!info.layer.getData(pixel);
            } catch (e) {
                return false;
            }
        });
    }

    map.getTargetElement().style.cursor = hasFeature ? 'pointer' : '';
});

function showFeaturePopup(featureData, coordinate) {
    const popup = document.getElementById('featurePopup');
    const popupBody = document.getElementById('featurePopupBody');
    const popupHeader = document.querySelector('.feature-popup-header h4');

    const feature = featureData.feature;
    const layerInfo = featureData.layerInfo;

    // Update header with layer name and feature count
    const featureCount = clickedFeatures.length;
    if (featureCount > 1) {
        popupHeader.textContent = `Feature ${currentFeatureIndex + 1} of ${featureCount} - ${layerInfo.name}`;
    } else {
        popupHeader.textContent = `${layerInfo.name}`;
    }

    // Raster identify entries carry pre-formatted band values;
    // vector features expose their properties
    let displayProperties = {};
    if (featureData.isRaster) {
        displayProperties = featureData.values;
    } else {
        const properties = feature.getProperties();

        // Remove geometry from properties display
        for (const key in properties) {
            if (key !== 'geometry' && key !== 'layer' && key !== 'extent') {
                displayProperties[key] = properties[key];
            }
        }
    }

    // Build properties HTML
    if (Object.keys(displayProperties).length === 0) {
        popupBody.innerHTML = '<div class="feature-popup-empty">No properties available</div>';
    } else {
        let html = '';

        // Add navigation buttons if multiple features
        if (featureCount > 1) {
            const isFirst = currentFeatureIndex === 0;
            const isLast = currentFeatureIndex === featureCount - 1;
            const prevDisabled = isFirst ? 'disabled' : '';
            const nextDisabled = isLast ? 'disabled' : '';

            html += `
                <div class="feature-popup-nav">
                    <button onclick="window.previousFeature()" class="feature-popup-nav-btn" title="Previous feature (←)" ${prevDisabled}>◀</button>
                    <span class="feature-popup-nav-count">${currentFeatureIndex + 1} of ${featureCount}</span>
                    <button onclick="window.nextFeature()" class="feature-popup-nav-btn" title="Next feature (→)" ${nextDisabled}>▶</button>
                </div>
            `;
        }

        // Start properties container with table
        html += '<div class="feature-popup-properties"><table class="feature-popup-table"><tbody>';

        for (const [key, value] of Object.entries(displayProperties)) {
            // Format the value
            let displayValue = value;
            if (value === null || value === undefined) {
                displayValue = '<em>null</em>';
            } else if (typeof value === 'object') {
                displayValue = JSON.stringify(value);
            } else {
                displayValue = String(value);
            }

            html += `
                <tr>
                    <td class="feature-popup-key">${escapeHtml(key)}</td>
                    <td class="feature-popup-value">${escapeHtml(displayValue)}</td>
                </tr>
            `;
        }

        // Close table and properties container
        html += '</tbody></table></div>';

        popupBody.innerHTML = html;
    }

    // Position popup near the click (only if coordinate provided, otherwise keep current position)
    if (coordinate) {
        const pixel = map.getPixelFromCoordinate(coordinate);
        popup.style.left = `${pixel[0] + 10}px`;
        popup.style.top = `${pixel[1] + 10}px`;
    } else if (lastClickCoordinate) {
        // Use last known coordinate for navigation
        const pixel = map.getPixelFromCoordinate(lastClickCoordinate);
        popup.style.left = `${pixel[0] + 10}px`;
        popup.style.top = `${pixel[1] + 10}px`;
    }

    // Show popup
    popup.classList.add('visible');
}

// Navigation functions for multiple features
window.nextFeature = function() {
    if (clickedFeatures.length > 0 && currentFeatureIndex < clickedFeatures.length - 1) {
        currentFeatureIndex++;
        showFeaturePopup(clickedFeatures[currentFeatureIndex], null);
    }
};

window.previousFeature = function() {
    if (clickedFeatures.length > 0 && currentFeatureIndex > 0) {
        currentFeatureIndex--;
        showFeaturePopup(clickedFeatures[currentFeatureIndex], null);
    }
};

function hideFeaturePopup() {
    const popup = document.getElementById('featurePopup');
    popup.classList.remove('visible');
    clickedFeatures = [];
    currentFeatureIndex = 0;
    lastClickCoordinate = null;

    // Reset transform and size when hiding
    popup.style.transform = '';
    popup.style.width = '';
    popup.style.height = '';
}

// Close popup when close button clicked
document.getElementById('featurePopupClose').addEventListener('click', hideFeaturePopup);

// Keyboard shortcuts for popup
document.addEventListener('keydown', function(e) {
    const popup = document.getElementById('featurePopup');

    if (!popup.classList.contains('visible')) {
        return;
    }

    // Escape key - close popup
    if (e.key === 'Escape') {
        hideFeaturePopup();
    }

    // Arrow keys - navigate between features
    if (clickedFeatures.length > 1) {
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            window.previousFeature();
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            window.nextFeature();
        }
    }
});

// Make popup draggable
(function() {
    const popup = document.getElementById('featurePopup');
    const header = document.querySelector('.feature-popup-header');

    let isDragging = false;
    let currentX;
    let currentY;
    let initialX;
    let initialY;
    let xOffset = 0;
    let yOffset = 0;

    header.addEventListener('mousedown', dragStart);
    document.addEventListener('mousemove', drag);
    document.addEventListener('mouseup', dragEnd);

    function dragStart(e) {
        // Don't drag if clicking on close button
        if (e.target.id === 'featurePopupClose') {
            return;
        }

        initialX = e.clientX - xOffset;
        initialY = e.clientY - yOffset;

        if (e.target === header || header.contains(e.target)) {
            isDragging = true;
        }
    }

    function drag(e) {
        if (isDragging) {
            e.preventDefault();

            currentX = e.clientX - initialX;
            currentY = e.clientY - initialY;

            xOffset = currentX;
            yOffset = currentY;

            setTranslate(currentX, currentY, popup);
        }
    }

    function dragEnd(e) {
        initialX = currentX;
        initialY = currentY;

        isDragging = false;
    }

    function setTranslate(xPos, yPos, el) {
        el.style.transform = `translate3d(${xPos}px, ${yPos}px, 0)`;
    }
})();

// Helper to escape HTML
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

// ===== End Feature Click Handler =====

// Helper function to load file based on extension
async function loadFileByType(file) {
    const fileName = file.name.toLowerCase();

    if (fileName.endsWith('.pmtiles')) {
        await loadPMTilesFromFile(file);
    } else if (fileName.endsWith('.geojson') || fileName.endsWith('.json')) {
        await loadGeoJSONFromFile(file);
    } else if (fileName.endsWith('.fgb')) {
        await loadFlatGeobufFromFile(file);
    } else if (fileName.endsWith('.parquet') || fileName.endsWith('.geoparquet')) {
        await loadGeoParquetFromFile(file);
    } else {
        showStatus('Error: Unsupported file type. Please use .pmtiles, .geojson, .json, .fgb, or .parquet files', true);
    }
}

// File input event listener for local files
document.getElementById('fileInput').addEventListener('change', async function(e) {
    const file = e.target.files[0];
    if (file) {
        await loadFileByType(file);

        // Clear the file input so the same file can be selected again if needed
        e.target.value = '';
    }
});

// Drag and drop support for drop zone
const dropZone = document.getElementById('dropZone');
const fileInput = document.getElementById('fileInput');

// Click drop zone to open file picker
dropZone.addEventListener('click', () => {
    fileInput.click();
});

// Keyboard support for drop zone (Enter and Space)
dropZone.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        fileInput.click();
    }
});

// Prevent default drag behaviors
['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
    dropZone.addEventListener(eventName, preventDefaults, false);
});

function preventDefaults(e) {
    e.preventDefault();
    e.stopPropagation();
}

// Visual feedback for drag over
['dragenter', 'dragover'].forEach(eventName => {
    dropZone.addEventListener(eventName, () => {
        dropZone.classList.add('drag-over');
    }, false);
});

['dragleave', 'drop'].forEach(eventName => {
    dropZone.addEventListener(eventName, () => {
        dropZone.classList.remove('drag-over');
    }, false);
});

// Handle dropped files
dropZone.addEventListener('drop', async (e) => {
    const files = e.dataTransfer.files;
    if (files.length > 0) {
        const file = files[0];
        await loadFileByType(file);
    }
});

// Example URLs (for reference in console)
console.log('Example COG URL:', 'https://sentinel-cogs.s3.us-west-2.amazonaws.com/sentinel-s2-l2a-cogs/21/U/YT/2025/10/S2C_21UYT_20251003_0_L2A/TCI.tif');
console.log('Example Vector Tiles URL:', 'https://demotiles.maplibre.org/tiles/{z}/{x}/{y}.pbf');
console.log('Example PMTiles URL:', 'https://overturemaps-tiles-us-west-2-beta.s3.amazonaws.com/2024-12-18/places.pmtiles');
console.log('Example GeoJSON URL:', 'https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/all_day.geojson');
console.log('Example FlatGeobuf URL:', 'https://flatgeobuf.org/test/data/UScounties.fgb');
console.log('');
console.log('Example GeoParquet URL:', 'https://data.source.coop/vida/google-microsoft-open-buildings/geoparquet/by_country/country_iso=USA/part0.parquet');
console.log('');
console.log('Load with query params: ?url=YOUR_URL&type=cog|vector|pmtiles|geojson|flatgeobuf|geoparquet');
